import React, { Fragment, useMemo, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Tab, Transition, Dialog } from '@headlessui/react'
import { 
  XMarkIcon, ChevronDownIcon, ClockIcon, MapPinIcon, 
  UserIcon, BuildingOfficeIcon, TruckIcon, DocumentTextIcon,
  PhotoIcon, ChatBubbleLeftRightIcon, CheckCircleIcon,
  ExclamationTriangleIcon, LockClosedIcon, PaperAirplaneIcon,
  ShoppingCartIcon, CurrencyDollarIcon, ClipboardDocumentListIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { FiPackage, FiUser, FiMapPin, FiClock, FiTruck, FiMessageSquare, FiCheck, FiAlertCircle } from 'react-icons/fi'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import SelectDropdown from '../components/SelectDropdown'
import request from '../utils/request'
import { getCoordinates } from '../utils/geocode'
import { formatDate, formatDateTimePST, formatEntryInstructionsLastUpdated, resolveEntryInstructionsLastUpdated } from '../utils/formatDate'
import { format } from 'date-fns'
import { fetchOrderImages } from '../utils/fetchOrderImages'
import ProductCardsDispatch from '../components/ProductCardsDispatch'
import CleaningReportPanel from './CleaningReportPanel'
import { LockboxDetailsLauncher } from './lockbox'
import { etaFromOrder } from '../utils/guestEta'
import { buildDriverStats, getDriverStats } from '../utils/driverStatsFromCompletedOrders'
import ChangeDeliveryFee from './ChangeDeliveryFee'
import parseUserInputAddress from '../utils/parseUserInputAddress'
import { isDoorDeliveryOrder } from '../utils/dispatchOrderBuckets'
import {
  getOrderGuestDisplayName,
  getOrderPlacedByName,
  getReservationHolderName,
} from '../utils/orderGuestName'
// RefundReviewModal has been folded into the new "Refunds" tab below; the
// standalone review modal is no longer launched from this drawer.

// Guest-provided delivery addresses (JSON-encoded) are stored on
// `order.userInputAddress` for this property. When we detect it, we render
// a dedicated, easy-to-scan card for dispatchers.
const GUEST_ADDRESS_PROPERTY_ID = '6626fba3974819b390186d9d'

const DEFAULT_STORE_ARRIVAL_HOUR = 12
const DEFAULT_STORE_ARRIVAL_MINUTE = 30

function resolveStoreArrivalTime(storeArrivalTime, inStockDate) {
  if (storeArrivalTime) return new Date(storeArrivalTime)
  if (!inStockDate) return null
  const d = new Date(inStockDate)
  d.setHours(DEFAULT_STORE_ARRIVAL_HOUR, DEFAULT_STORE_ARRIVAL_MINUTE, 0, 0)
  return d
}

function resolvePreferredHostDeliveryTime(hostSetDeliveryTime) {
  if (typeof hostSetDeliveryTime === 'string' && hostSetDeliveryTime.trim()) {
    return hostSetDeliveryTime.trim()
  }
  return null
}

function HostTimeWindowDisplay({ hostSetDeliveryTime, compact = false, inline = false, inlineBorderClass = 'border-violet-200/80' }) {
  const hostTimeWindow = resolvePreferredHostDeliveryTime(hostSetDeliveryTime)

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-gray-600 min-w-0">
        <FiClock className="w-4 h-4 text-violet-500 shrink-0" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 shrink-0">Host Time Window</span>
        <span className={`truncate ${hostTimeWindow ? 'text-gray-900' : 'text-gray-400 italic'}`}>
          {hostTimeWindow || 'Not provided by host'}
        </span>
      </div>
    )
  }

  if (inline) {
    return (
      <div className={`mt-4 pt-4 border-t ${inlineBorderClass}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 mb-1">Host Time Window</p>
        <p className="text-xs text-violet-700/80 mb-1">Read-only — set by the host when submitting entry instructions.</p>
        <p className={`font-semibold text-base ${hostTimeWindow ? 'text-violet-900' : 'text-violet-400 italic'}`}>
          {hostTimeWindow || 'Host has not inputted a time window'}
        </p>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-400 to-purple-500 rounded-2xl blur opacity-20" />
      <div className="relative bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-200 rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-violet-500 flex items-center justify-center flex-shrink-0 shadow-lg">
            <ClockIcon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-violet-900 text-lg mb-1">Host Time Window</h3>
            <p className="text-xs text-violet-700/80 mb-1">Read-only — set by the host when submitting entry instructions.</p>
            <p className={`font-semibold text-xl ${hostTimeWindow ? 'text-violet-800' : 'text-violet-400 italic text-base'}`}>
              {hostTimeWindow || 'Host has not inputted a time window'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function mergeTimeOntoInStockDate(picked, inStockDate) {
  if (!picked) return null
  if (!inStockDate) return picked
  const merged = new Date(inStockDate)
  merged.setHours(picked.getHours(), picked.getMinutes(), picked.getSeconds(), picked.getMilliseconds())
  return merged
}

export default function DispatchOrderDrawer({
  order,
  addons = [],
  isOpen,
  refreshOrders,
  closeDrawer,
  drivers,
  properties,
  patchOrder,
  showBroadcast,
  showEditDriverPay = false,
  onDriverPaySet,
  completedOrders = [],
}) {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  const [storeArrivalTime, setStoreArrivalTime] = useState(() =>
    resolveStoreArrivalTime(order.storeArrivalTime, order.inStockDate)
  )
  const [storeLocation, setStoreLocation] = useState(order.storeLocation || '')
  const [entryInstructions, setEntryInstructions] = useState(order.entryInstructions || '')
  const [dispatchNotes, setDispatchNotes] = useState(order.dispatchNotes || '')
  const [internalNotes, setInternalNotes] = useState(order.internalNotes || '')
  const [storeSpend, setStoreSpend] = useState(order.storeSpend || '')
  const [storeOrderId, setStoreOrderId] = useState(order.storeOrderId || '')
  const [storeOrderConfirmationUrl, setStoreOrderConfirmationUrl] = useState(order.storeOrderConfirmationUrl || '')
  const [urlError, setUrlError] = useState(null)
  const [company, setCompany] = useState(order.company || 'Ralphs')
  const [groceryOrderName, setGroceryOrderName] = useState('') // Not saved to DB, concatenated to dispatch notes
  const [deliveryWindow, setDeliveryWindow] = useState('') // Delivery only: time window e.g. 7am-11:30PM, concatenated to internalNotes
  const [imageUrls, setImageUrls] = useState([])
  const [addedCount, setAddedCount] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [krogerRelinkRequired, setKrogerRelinkRequired] = useState(false)
  const [hosts, setHosts] = useState([])
  const [openHistory, setOpenHistory] = useState(false)
  const [allowDateChange, setAllowDateChange] = useState(false)
  // True once the operator interacts with the Concierge Arrival Time picker.
  // Used so pre-dispatch saves don't persist the unsaved 12:30 PM default.
  const [arrivalTouched, setArrivalTouched] = useState(false)
  const [suggestedDrivers, setSuggestedDrivers] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [needsEntry, setNeedsEntry] = useState(order.needsEntryInstructions || false)
  const [needsTime, setNeedsTime] = useState(order.needsDeliveryTime || false)
  const [confirmMessage, setConfirmMessage] = useState(null)
  const [confirmError, setConfirmError] = useState(null)
  const [predictedPay, setPredictedPay] = useState(null)
  const [payError, setPayError] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [refundFilterActive, setRefundFilterActive] = useState(false)
  // The legacy RefundReviewModal has been replaced by the Refunds tab, so
  // the old `refundReviewOpen` state is no longer needed.
  // Refunds tab (bulk) state: shared reason/notes apply to every selected
  // item, and selection is a Set of `${orderId}:${idx}` keys so we can
  // multi-select across main + addons.
  const [bulkRefundSelection, setBulkRefundSelection] = useState(() => new Set())
  const [bulkRefundReason, setBulkRefundReason] = useState('')
  const [bulkRefundNotes, setBulkRefundNotes] = useState('')
  const [bulkRefundSending, setBulkRefundSending] = useState(false)
  const [bulkRefundMarking, setBulkRefundMarking] = useState(false)
  const [bulkRefundError, setBulkRefundError] = useState('')
  const [bulkRefundMessage, setBulkRefundMessage] = useState('')
  const [bulkRefundPreviewLoading, setBulkRefundPreviewLoading] = useState(false)
  const [bulkRefundPreviewError, setBulkRefundPreviewError] = useState('')
  const [bulkRefundPreviewByOrder, setBulkRefundPreviewByOrder] = useState({})
  const [selectedProperty, setSelectedProperty] = useState(
    order.propertyID ? { label: `${order.property.address.line1} ${order.property.address.city}`, value: order.propertyID } : null
  )
  const [propertyUnlocked, setPropertyUnlocked] = useState(false)
  
  // Legacy single-product refund state removed — the Refunds tab handles
  // reason/notes/selection in bulk below.

  const { adminInfo } = useSelector((state) => state.login)
  const [selectedDispatcher, setSelectedDispatcher] = useState(`${adminInfo.firstName} ${adminInfo.lastName}`)
  const previousOrderHostStateRef = useRef({
    entryInstructions: order.entryInstructions || '',
    storeArrivalTime: order.storeArrivalTime || null,
    inStockDate: order.inStockDate || null,
    needsEntryInstructions: Boolean(order.needsEntryInstructions),
    needsDeliveryTime: Boolean(order.needsDeliveryTime),
  })

  // LocalStorage key for this order's draft
  const draftKey = `dispatchDraft_${order._id}`

  // Load saved draft on mount — storeArrivalTime is not restored from draft; it comes from the order or inStockDate + 12:30 PM default
  useEffect(() => {
    const saved = localStorage.getItem(draftKey)
    if (saved) {
      try {
        const draft = JSON.parse(saved)
        if (draft.storeLocation) setStoreLocation(draft.storeLocation)
        if (draft.entryInstructions) setEntryInstructions(draft.entryInstructions)
        if (draft.dispatchNotes) setDispatchNotes(draft.dispatchNotes)
        if (draft.internalNotes) setInternalNotes(draft.internalNotes)
        if (draft.storeSpend) setStoreSpend(draft.storeSpend)
        if (draft.storeOrderConfirmationUrl) setStoreOrderConfirmationUrl(draft.storeOrderConfirmationUrl)
        if (draft.company) setCompany(draft.company)
        if (draft.groceryOrderName) setGroceryOrderName(draft.groceryOrderName)
        if (draft.deliveryWindow) setDeliveryWindow(draft.deliveryWindow)
        if (draft.needsEntry !== undefined && !(draft.entryInstructions || order.entryInstructions || '').trim()) {
          setNeedsEntry(draft.needsEntry)
        }
        if (draft.needsTime !== undefined) setNeedsTime(draft.needsTime)
        if (draft.selectedDispatcher) setSelectedDispatcher(draft.selectedDispatcher)
      } catch (e) {
        console.error('Failed to load draft:', e)
      }
    }
  }, [draftKey])

  useEffect(() => {
    const previous = previousOrderHostStateRef.current
    const next = {
      entryInstructions: order.entryInstructions || '',
      storeArrivalTime: order.storeArrivalTime || null,
      inStockDate: order.inStockDate || null,
      needsEntryInstructions: Boolean(order.needsEntryInstructions),
      needsDeliveryTime: Boolean(order.needsDeliveryTime),
    }

    if (previous.entryInstructions !== next.entryInstructions) {
      setEntryInstructions(next.entryInstructions)
      if (next.entryInstructions) {
        setNeedsEntry(false)
      }
    }

    if (
      previous.storeArrivalTime !== next.storeArrivalTime ||
      previous.inStockDate !== next.inStockDate
    ) {
      setStoreArrivalTime(resolveStoreArrivalTime(next.storeArrivalTime, next.inStockDate))
    }

    if (previous.needsEntryInstructions !== next.needsEntryInstructions) {
      setNeedsEntry(next.entryInstructions ? false : next.needsEntryInstructions)
    }

    if (previous.needsDeliveryTime !== next.needsDeliveryTime) {
      setNeedsTime(next.needsDeliveryTime)
    }

    previousOrderHostStateRef.current = next
  }, [
    order.entryInstructions,
    order.inStockDate,
    order.needsDeliveryTime,
    order.needsEntryInstructions,
    order.storeArrivalTime,
  ])

  useEffect(() => {
    if (entryInstructions.trim()) {
      setNeedsEntry(false)
    }
  }, [entryInstructions])

  useEffect(() => {
    setSelectedProperty(
      order.propertyID
        ? {
            label: [order.property?.address?.line1, order.property?.address?.city]
              .filter(Boolean)
              .join(' '),
            value: order.propertyID,
          }
        : null
    )
    setPropertyUnlocked(false)
    setArrivalTouched(false)
  }, [order._id, order.propertyID, order.property?.address?.line1, order.property?.address?.city])

  // Store location display: append property zipCode for visibility when not already at end
  const zipCode = order.property?.address?.zipCode || ''
  const storeLocationEndsWithZip = zipCode && (storeLocation.trim().endsWith(zipCode) || /\d{5}(-\d{4})?$/.test(storeLocation.trim()))
  const storeLocationDisplay = !storeLocationEndsWithZip && zipCode ? `${storeLocation}${storeLocation ? ', ' : ''}${zipCode}` : storeLocation
  const handleStoreLocationChange = (e) => {
    let v = e.target.value
    if (zipCode) {
      if (v.endsWith(', ' + zipCode)) v = v.slice(0, -(', ' + zipCode).length).trim()
      else if (v.endsWith(zipCode)) v = v.slice(0, -zipCode.length).trim()
    }
    setStoreLocation(v)
  }

  // Auto-save draft when fields change
  useEffect(() => {
    const draft = {
      storeLocation,
      entryInstructions,
      dispatchNotes,
      internalNotes,
      storeSpend,
      storeOrderConfirmationUrl,
      company,
      groceryOrderName,
      deliveryWindow,
      needsEntry,
      needsTime,
      storeArrivalTime: storeArrivalTime?.toISOString(),
      selectedDispatcher,
      savedAt: new Date().toISOString()
    }
    localStorage.setItem(draftKey, JSON.stringify(draft))
  }, [draftKey, storeLocation, entryInstructions, dispatchNotes, internalNotes, storeSpend, storeOrderConfirmationUrl, company, groceryOrderName, deliveryWindow, needsEntry, needsTime, storeArrivalTime, selectedDispatcher])

  // Clear draft on successful save
  const clearDraft = () => {
    localStorage.removeItem(draftKey)
  }

  const entryHistory = (order.property.entryInstructionsHistory || [])
    .slice()
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .map(({ previous, updatedAt }) => ({ text: previous, date: new Date(updatedAt) }))

  const resolvedEntryInstructions = (entryInstructions || order.entryInstructions || order.property?.description || '').trim()
  const entryInstructionsLastUpdatedLabel = formatEntryInstructionsLastUpdated(
    resolveEntryInstructionsLastUpdated(order)
  )

  const allOrders = [order, ...addons]
  const totalCount = allOrders.flatMap(o => o.products || []).reduce((sum, p) => sum + (p.quantity || 1), 0)
  const refundProducts = allOrders.flatMap(o => o.products || []).filter(p => p.substituteOrRefund === 'refund')
  const refundCount = refundProducts.reduce((sum, p) => sum + (p.quantity || 1), 0)
  const [totalAddedCount, setTotalAddedCount] = useState(totalCount)
  const originalDate = order.inStockDate ? new Date(order.inStockDate) : null
  const [dateChanged, setDateChanged] = useState(false)

  const propertyAccessLabel = order?.property?.name || order?.property?.nickname || order?.property?.address?.line1 || (order?.propertyID ? `Property ${order.propertyID}` : undefined)

  // Parse the guest-provided delivery address (JSON-stuffed string) when
  // present so we can render it in a dedicated card for the property that
  // relies on it. Falls back gracefully for everything else.
  const guestAddress = useMemo(() => {
    const propId = typeof order?.propertyID === 'object' ? (order.propertyID?._id || '') : (order?.propertyID || '')
    if (String(propId) !== GUEST_ADDRESS_PROPERTY_ID) return null
    const { parsed } = parseUserInputAddress(order?.userInputAddress || '')
    return parsed
  }, [order?.propertyID, order?.userInputAddress])

  // Items marked for refund, with parent order id/index so we can send a
  // templated email + (optionally) trigger refunds from one place.
  const refundLineItems = useMemo(() => {
    const items = []
    for (const o of allOrders) {
      ;(o.products || []).forEach((p, idx) => {
        if (p.substituteOrRefund === 'refund' || p.refunded) {
          items.push({
            orderId: o._id,
            orderUniqueId: o.uniqueId,
            productIndex: idx,
            product: p,
          })
        }
      })
    }
    return items
  }, [allOrders])

  // Body scroll is locked by Headless UI Dialog (for the order modal) and by
  // ChatPanel (via its own useLockBodyScroll). No need for a third lock here,
  // which previously caused flicker / "blurred" overlay states.

  // ═══════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════
  const host = hosts.find(h => h._id === order.property?.hostID) || {}
  const hostName = host.firstName ? `${host.firstName} ${host.lastName}` : 'N/A'
  const hostNotes = (host.internalHostNotes || '').trim()
  const guestNote = (order.guestNote || '').trim()
  const dispatched = isDispatched(order)
  const hasAddons = addons.length > 0
  // Store order reference (confirmation URL or legacy ID) is what moves an
  // order out of Awaiting Dispatch. Without it, Save Changes persists edits
  // (arrival time, addresses, notes) but leaves the order on the same tab.
  const hasStoreOrderReference = Boolean(storeOrderConfirmationUrl.trim() || storeOrderId.trim())

  const propertyOptions = properties.map(p => ({
    label: [p.address?.line1, p.address?.line2, p.address?.city, p.address?.state, p.address?.zipCode]
      .filter(Boolean)
      .join(' '),
    value: p._id
  }))


  // ═══════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════
  
  // Haversine for distance calculation
  const haversine = (c1, c2) => {
    const toRad = x => x * Math.PI / 180
    const R = 3958.8
    const dLat = toRad(c2.lat - c1.lat)
    const dLon = toRad(c2.lng - c1.lng)
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(c1.lat)) * Math.cos(toRad(c2.lat)) * Math.sin(dLon / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(a))
  }

  useEffect(() => {
    const prop = order.property?.address
    if (!prop?.lat || !prop?.lng) return

    const φ = prop.lat * Math.PI / 180
    const latDelta = 20 / 69
    const lngDelta = 20 / (Math.cos(φ) * 69)
    const minLat = prop.lat - latDelta, maxLat = prop.lat + latDelta
    const minLng = prop.lng - lngDelta, maxLng = prop.lng + lngDelta

    // Compute driver stats from completed orders (driverID may be string or populated object { _id })
    const driverStats = buildDriverStats(completedOrders);

    const nearby = drivers
      .filter(d => d.currentAddress?.lat >= minLat && d.currentAddress?.lat <= maxLat && d.currentAddress?.lng >= minLng && d.currentAddress?.lng <= maxLng)
      .map(d => {
        const stats = getDriverStats(d, driverStats);
        return {
          ...d,
          distance: haversine({ lat: prop.lat, lng: prop.lng }, { lat: d.currentAddress.lat, lng: d.currentAddress.lng }),
          orderCount: stats.orderCount,
          lastOrderDate: stats.lastOrderDate,
          lastOrderAddress: stats.lastOrderAddress,
        };
      })
      .filter(d => d.distance <= 20)
    nearby.sort((a, b) => a.distance - b.distance)
    setSuggestedDrivers(nearby)
  }, [order.property, drivers, completedOrders])

  useEffect(() => {
    async function fetchHostsAndAdmins() {
      try {
        const res = await request.get('/hosts')
        if (!res || !res.data) return
        const allHosts = res.data
        setHosts(allHosts)
      } catch (err) {
        console.error('Error fetching hosts/admins:', err)
      }
    }
    fetchHostsAndAdmins()
  }, [])

  useEffect(() => {
    function handleMessage(e) {
      if (e.origin !== window.origin) return
      if (typeof e.data.addedCount === 'number') {
        setAddedCount(e.data.addedCount)
        setTotalAddedCount(e.data.totalCount)
      }
      if (e.data.cleared) {
        setAddedCount(0)
        setTotalAddedCount(0)
        setMessage('✅ Cart cleared')
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    if (!originalDate || !storeArrivalTime) { setDateChanged(false); return }
    const origDay = originalDate.toDateString()
    const pickedDay = storeArrivalTime.toDateString()
    setDateChanged(origDay !== pickedDay)
  }, [storeArrivalTime, originalDate])

  useEffect(() => {
    if (!isOpen) return
    setPredictedPay(null)
    setPayError(null)
    request.get(`/orders/predict/${order._id}`)
      .then(res => setPredictedPay(res.data.predictedPay))
      .catch(() => setPayError("Could not load prediction"))
  }, [isOpen, order.uniqueId])

  useEffect(() => {
    if (!isOpen) { setImageUrls([]); return }
    fetchOrderImages(order.uniqueId)
      .then(urls => setImageUrls(urls))
      .catch(err => console.error('❌ image fetch error', err))
  }, [isOpen, order._id])

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  function isDispatched(o) {
    if (o.status === 'completed' || o.status === 'canceled' || o.status === 'cancelled') return false
    const hasDriver = !!o.driverID
    const spend = parseFloat(o.storeSpend)
    const hasValidSpend = !isNaN(spend) && spend > 0
    const hasOrderId = o.storeOrderId?.trim().length > 0
    if (isDoorDeliveryOrder(o)) return hasValidSpend && hasOrderId
    return hasDriver && hasValidSpend && hasOrderId
  }

  async function handleBroadcast() {
    for (const d of suggestedDrivers) {
      const firstName = d.name.split(' ')[0]
      const msg = `Hey! ${firstName}, we have an order coming up and wanted to see if you would like to take it. Here are the details!

Property: ${order.property.address.line1}, ${order.property.address.city}
Store Location: ${order.storeLocation}
Driver Pay: $${(order.predictedPay ?? 0).toFixed(2)}
Delivery Date: ${formatDate(order.inStockDate)}
Arrival Time: ${order.storeArrivalTime || '–'}

Please accept as soon as you can as all orders are now first come first serve. Thank you!

– ${adminInfo.firstName}`.trim()

      await request.post('/admin/send-push-notification', {
        driverId: d.id,
        title: `New Order Available`,
        body: msg
      })
    }
    setBroadcastOpen(false)
  }

  async function handleResendConfirmation() {
    setConfirmMessage(null)
    setConfirmError(null)
    try {
      await request.post(`/orders/${order._id}/resend-invoice`)
      setConfirmMessage('Confirmation email sent!')
    } catch (err) {
      console.error(err)
      setConfirmError('Failed to resend confirmation.')
    }
  }

  // URL validation helper
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Store company configurations
  const STORE_CONFIGS = {
    Ralphs: {
      urlPattern: (orderId) => `https://www.ralphs.com/mypurchases/pending/${orderId}`,
      domains: ['ralphs.com'],
    },
    Kroger: {
      urlPattern: (orderId) => `https://www.kroger.com/mypurchases/pending/${orderId}`,
      domains: ['kroger.com'],
    },
    Walmart: {
      urlPattern: (orderId) => `https://www.walmart.com/orders/${orderId}`,
      domains: ['walmart.com'],
    },
  };

  // Extract order number from URL
  const extractOrderNumber = (input) => {
    if (!input || !isValidUrl(input)) return null;
    const str = input.trim();
    
    // Extract long number from URL path
    const urlMatch = str.match(/\/(\d{10,})/);
    if (urlMatch) return urlMatch[1];
    
    return null;
  };

  // Auto-detect company from URL
  const detectCompanyFromUrl = (url) => {
    if (!url) return null;
    const lowerUrl = url.toLowerCase();
    
    for (const [companyName, config] of Object.entries(STORE_CONFIGS)) {
      if (config.domains.some(domain => lowerUrl.includes(domain))) {
        return companyName;
      }
    }
    return null;
  };

  // Generate order URL based on company and order number
  const generateOrderUrl = (orderNumber, companyName) => {
    const config = STORE_CONFIGS[companyName];
    if (!config || !orderNumber) return null;
    return config.urlPattern(orderNumber);
  };

  // Get the extracted order number and generated URL
  const extractedOrderNumber = extractOrderNumber(storeOrderConfirmationUrl);
  const generatedOrderUrl = extractedOrderNumber ? generateOrderUrl(extractedOrderNumber, company) : null;

  async function handleSubmit() {
    let err = null
    const spendVal = parseFloat(storeSpend)
    const trimmedUrl = storeOrderConfirmationUrl.trim()
    const isDelivery = isDoorDeliveryOrder(order)

    // Two save modes:
    //  - Pre-dispatch (no store order reference yet): the order stays in
    //    Awaiting Dispatch. We still let the operator save concierge arrival
    //    time, store/property address, dispatch + internal notes, entry
    //    instructions, etc. Spend/URL/arrival-time are NOT required here.
    //  - Dispatch (URL or legacy ID present): full validation as before, and
    //    the order moves to the next tab.
    const spendTouched =
      String(storeSpend ?? '').trim() !== '' &&
      String(storeSpend) !== String(order.storeSpend ?? '')
    const validateSpend = hasStoreOrderReference || spendTouched

    // Validate URL format whenever one is typed (partial/invalid URLs must not be saved)
    if (trimmedUrl && !isValidUrl(trimmedUrl)) {
      err = 'Store Order Confirmation URL must be a valid URL (http:// or https://).'
    }

    if (!err && validateSpend && !/^[\d]+\.[\d]{2}$/.test(storeSpend)) err = 'Store Spend must have exactly 2 decimal places (e.g. 12.34).'
    else if (!err && validateSpend && (isNaN(spendVal) || spendVal <= 0)) err = 'Store Spend must be a positive number.'
    else if (!err && hasStoreOrderReference && !isDelivery && !storeArrivalTime) err = 'Store Arrival Time is required.'

    if (err) { setError(err); return }

    const guestNote = (order.guestNote || '').trim()
    let fullDispatchNotes
    if (guestNote) {
      const guestNoteBlock = `Guest Notes:\n${guestNote}`
      if (dispatchNotes.includes(guestNoteBlock)) fullDispatchNotes = dispatchNotes.trim()
      else {
        const cleaned = dispatchNotes.replace(/\n*Guest Notes:[\s\S]*$/m, '').trim()
        fullDispatchNotes = `${cleaned}\n\n${guestNoteBlock}`.trim()
      }
    } else fullDispatchNotes = dispatchNotes.trim()

    const originalDispatchNotes = (order.dispatchNotes || '').trim()
    const dispatchNotesChanged = fullDispatchNotes !== originalDispatchNotes

    let storeLat = order.storeLat
    let storeLng = order.storeLng
    if (storeLocation.trim() && storeLocation.trim() !== order.storeLocation?.trim()) {
      const coords = await getCoordinates(storeLocation)
      if (coords) { storeLat = coords.lat; storeLng = coords.lng }
    }

    const internalNotesToSave = isDelivery && deliveryWindow.trim()
      ? (internalNotes.trim() + '\nDelivery Window: ' + deliveryWindow.trim()).trim()
      : internalNotes

    const trimmedEntryInstructions = entryInstructions.trim()
    const resolvedEntryInstructionsToSave = trimmedEntryInstructions || order.property.description || ''
    const entryInstructionsSaved = Boolean(resolvedEntryInstructionsToSave.trim())

    // Pre-dispatch, only persist the concierge arrival time if the operator
    // actually set it. Otherwise the drawer's 12:30 PM default would be
    // written to the order on every notes/address save.
    const sendArrivalTime = !isDelivery && (hasStoreOrderReference || arrivalTouched)

    const mainPayload = {
      storeArrivalTime: isDelivery ? order.storeArrivalTime : (sendArrivalTime ? storeArrivalTime : undefined),
      storeLocation,
      storeLat,
      storeLng,
      entryInstructions: resolvedEntryInstructionsToSave,
      internalNotes: internalNotesToSave,
      propertyID: selectedProperty?.value,
      dispatcher: selectedDispatcher,
      isAddOn: false,
      needsEntryInstructions: entryInstructionsSaved ? false : needsEntry,
      needsDeliveryTime: needsTime
    }

    if (validateSpend) mainPayload.storeSpend = parseFloat(spendVal.toFixed(2))

    // Store order reference fields are only written once the operator has
    // placed the order. Omitting them pre-dispatch keeps the order in the
    // Awaiting Dispatch bucket (which keys off storeOrderId / storeSpend).
    if (hasStoreOrderReference) {
      mainPayload.storeOrderId = trimmedUrl || storeOrderId // Use URL if provided, fallback to legacy ID
      mainPayload.storeOrderConfirmationUrl = trimmedUrl || null // New URL field
      mainPayload.company = trimmedUrl ? company : order.company // Save store/company name
      mainPayload.placedAt = !order.storeOrderId ? new Date().toISOString() : order.placedAt // Track when order was placed
    }

    if (
      entryInstructionsSaved &&
      trimmedEntryInstructions !== (order.entryInstructions || '').trim()
    ) {
      mainPayload.entryInstructionsLastUpdated = new Date().toISOString()
    }

    // Door-delivery orders move to `requested` as soon as the operator
    // has placed the store handoff (URL or legacy ID). We deliberately
    // dropped the `spendVal > 0` clause here: door deliveries often have
    // no third-party cart, and the `awaiting` / `dispatched` buckets are
    // now door-aware so the row stays in Dispatched after refresh
    // (see `isDoorDeliveryDispatched`).
    if (isDelivery && hasStoreOrderReference) {
      mainPayload.status = 'requested'
    }

    if (dispatchNotesChanged) mainPayload.dispatchNotes = fullDispatchNotes || 'No Additional Notes'

    try {
      if (addons.length > 0) {
        const orderGroupItems = [order, ...addons].map(o => ({ id: o._id, uniqueId: o.uniqueId }))
        const parentPayload = { ...mainPayload, orderGroupItems }
        const addonBase = { ...mainPayload, isAddOn: true, originalOrderId: order._id }
        // Add-ons only get the placeholder spend once the group is actually dispatched.
        if (hasStoreOrderReference) addonBase.storeSpend = '0.01'
        else delete addonBase.storeSpend
        await patchOrder(order._id, parentPayload)
        await Promise.all(addons.map(a => patchOrder(a._id, addonBase)))
      } else {
        await patchOrder(order._id, mainPayload)
      }
    } catch (e) {
      // Surface the real backend message instead of optimistically merging
      // a save that didn't actually persist. Keep the drawer open so the
      // operator can fix and retry.
      const serverMessage =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to save dispatch update.'
      setError(serverMessage)
      return
    }
    setMessage('Order updated.')
    clearDraft() // Clear saved draft on successful save
    refreshOrders({ ...order, ...mainPayload })
    closeDrawer()
  }

  useEffect(() => {
    if (!isOpen) return
    setKrogerRelinkRequired(false)
  }, [isOpen, order._id])

  useEffect(() => {
    function onKrogerWindowMessage(ev) {
      if (ev.origin !== window.location.origin) return
      if (ev.data?.type === 'kroger-linked') {
        setKrogerRelinkRequired(false)
        setError(null)
        setMessage('Kroger connected. Click Add to cart again to load items.')
      }
    }
    window.addEventListener('message', onKrogerWindowMessage)
    return () => window.removeEventListener('message', onKrogerWindowMessage)
  }, [])

  async function handleConnectKroger() {
    setError(null)
    setMessage(null)
    try {
      const redirectUri = `${window.location.origin}/kroger-callback`
      const { data } = await request.get('/orders/kroger/oauth-url', {
        params: { redirectUri, linkOnly: 'true' },
      })
      if (!data?.url) {
        setError('Could not start Kroger sign-in.')
        return
      }
      window.open(data.url, 'krogerOAuth', 'popup=yes,width=540,height=720')
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Could not start Kroger sign-in.'
      setError(msg)
    }
  }

  async function handleAddToKrogerCart() {
    setIsAdding(true)
    setError(null)
    setMessage(null)
    const summary = []
    
    try {
      for (const order of allOrders) {
        const orderId = order._id
        const orderItemCount = (order.products || []).reduce((s, p) => s + (p.quantity || 1), 0)
        try {
          const res = await request.post(`/orders/${orderId}/kroger/add`)
          summary.push({ 
            orderId, 
            success: true, 
            addedCount: res.data?.addedCount ?? orderItemCount,
            totalCount: res.data?.totalCount ?? orderItemCount,
            ...res.data 
          })
        } catch (err) {
          const errRes = err.response
          const errData = errRes?.data || {}
          const needsKrogerAuth = errRes?.status === 401 && errData.needsAuth === true
          if (needsKrogerAuth) {
            setKrogerRelinkRequired(true)
          }
          summary.push({ 
            orderId, 
            success: false, 
            addedCount: errData.addedCount || 0, 
            totalCount: errData.totalCount || orderItemCount, 
            failures: errData.failures || [],
            needsKrogerAuth,
            message: needsKrogerAuth
              ? 'Kroger authorization required — use Connect Kroger below, then click Add to cart again.'
              : (errData.message || errData.error || 'Failed to add items to Kroger cart'),
          })
        }
      }
      const added = summary.reduce((sum, r) => sum + (r.addedCount || 0), 0)
      const total = summary.reduce((sum, r) => sum + (r.totalCount || 0), 0)
      const failed = summary.filter(r => !r.success || (r.failures || []).length > 0 || (r.totalCount && r.addedCount < r.totalCount))

      setAddedCount(added)
      setTotalAddedCount(total)

      if (failed.length > 0 || added < total) {
        const authFail = failed.find(r => r.needsKrogerAuth)
        if (authFail) {
          setError(authFail.message)
        } else {
          const failureMessage = failed.find(r => r.message)?.message
          setError(failureMessage || `Added ${added} of ${total} items to Kroger cart.`)
        }
      } else {
        setKrogerRelinkRequired(false)
        setMessage('All items successfully added to Kroger cart.')
      }
    } finally {
      setIsAdding(false)
    }
  }

  function handleStoreArrivalTimeChange(picked) {
    setArrivalTouched(true)
    if (!picked) {
      setStoreArrivalTime(null)
      return
    }
    if (allowDateChange) {
      setStoreArrivalTime(picked)
      return
    }
    setStoreArrivalTime(mergeTimeOntoInStockDate(picked, order.inStockDate))
  }

  function handleAllowDateToggle() {
    setAllowDateChange(prev => {
      const turningOff = prev === true
      if (turningOff && storeArrivalTime && originalDate) {
        setStoreArrivalTime(mergeTimeOntoInStockDate(storeArrivalTime, order.inStockDate))
      }
      return !prev
    })
  }

  const handleAddHostNotes = () => {
    if (!hostNotes) return
    setDispatchNotes(prev => prev ? `${prev.trim()}\n\nHost Notes:\n${hostNotes}` : `Host Notes:\n${hostNotes}`)
  }

  // ─── Bulk Refund Handlers (Refunds tab) ──────────────────────────
  // Selection key format: `${orderId}:${productIndex}`. We build a flat list
  // of refundable rows across main + addon orders so one selection set drives
  // everything.
  const bulkKey = (orderId, idx) => `${orderId}:${idx}`

  const bulkRefundRows = useMemo(() => {
    const rows = []
    for (const o of allOrders) {
      ;(o.products || []).forEach((p, idx) => {
        rows.push({
          orderId: o._id,
          orderUniqueId: o.uniqueId,
          isAddon: !!o._isAddon,
          productIndex: idx,
          product: p,
          key: bulkKey(o._id, idx),
        })
      })
    }
    return rows
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders.map(o => o._id).join('|'), JSON.stringify(allOrders.map(o => (o.products || []).map(p => [p.refunded, p.refundApproved, p.refundReason])))])

  const bulkRefundSelectedRows = useMemo(
    () => bulkRefundRows.filter(r => bulkRefundSelection.has(r.key) && !r.product.refunded),
    [bulkRefundRows, bulkRefundSelection]
  )

  const bulkRefundTotal = useMemo(
    () => bulkRefundSelectedRows.reduce(
      (sum, r) => sum + (Number(r.product.price) || 0) * (Number(r.product.quantity) || 1),
      0
    ),
    [bulkRefundSelectedRows]
  )

  const bulkRefundPreviewGroups = useMemo(() => {
    const byOrder = new Map()

    for (const row of bulkRefundSelectedRows) {
      const existing = byOrder.get(row.orderId) || {
        orderId: row.orderId,
        orderUniqueId: row.orderUniqueId,
        isAddon: row.isAddon,
        indices: [],
        rows: [],
        total: 0,
      }

      existing.indices.push(row.productIndex)
      existing.rows.push(row)
      existing.total += (Number(row.product.price) || 0) * (Number(row.product.quantity) || 1)

      byOrder.set(row.orderId, existing)
    }

    return Array.from(byOrder.values())
      .map((group) => ({
        ...group,
        indices: group.indices.slice().sort((left, right) => left - right),
      }))
      .sort((left, right) => {
        if (left.isAddon !== right.isAddon) return Number(left.isAddon) - Number(right.isAddon)
        return String(left.orderUniqueId || '').localeCompare(String(right.orderUniqueId || ''))
      })
  }, [bulkRefundSelectedRows])

  useEffect(() => {
    let cancelled = false

    if (bulkRefundPreviewGroups.length === 0) {
      setBulkRefundPreviewByOrder({})
      setBulkRefundPreviewError('')
      setBulkRefundPreviewLoading(false)
      return undefined
    }

    const fetchRefundPreviews = async () => {
      setBulkRefundPreviewLoading(true)
      setBulkRefundPreviewError('')
      setBulkRefundPreviewByOrder({})

      try {
        const responses = await Promise.all(
          bulkRefundPreviewGroups.map(async (group) => {
            const { data } = await request.post(`/orders/${group.orderId}/refund-email-preview`, {
              indices: group.indices,
            })

            return [group.orderId, data?.preview || null]
          })
        )

        if (!cancelled) {
          setBulkRefundPreviewByOrder(Object.fromEntries(responses))
        }
      } catch (err) {
        console.error('Failed to load refund email preview:', err)
        if (!cancelled) {
          setBulkRefundPreviewByOrder({})
          setBulkRefundPreviewError(
            err?.response?.data?.error || err?.message || 'Failed to load the exact refund email preview.'
          )
        }
      } finally {
        if (!cancelled) {
          setBulkRefundPreviewLoading(false)
        }
      }
    }

    fetchRefundPreviews()

    return () => {
      cancelled = true
    }
  }, [bulkRefundPreviewGroups])

  const bulkRefundToggle = (row) => {
    if (row.product.refunded) return // already refunded rows are read-only
    setBulkRefundSelection(prev => {
      const next = new Set(prev)
      if (next.has(row.key)) next.delete(row.key)
      else next.add(row.key)
      return next
    })
  }

  const bulkRefundSelectAll = () => {
    setBulkRefundSelection(new Set(
      bulkRefundRows.filter(r => !r.product.refunded).map(r => r.key)
    ))
  }

  const bulkRefundSelectOutOfStock = () => {
    setBulkRefundSelection(new Set(
      bulkRefundRows
        .filter(r => !r.product.refunded && String(r.product.substituteOrRefund || '').toLowerCase() === 'refund')
        .map(r => r.key)
    ))
  }

  const bulkRefundClear = () => setBulkRefundSelection(new Set())

  // Group selected rows by orderId so we hit the backend once per order. The
  // backend endpoint does one Order.findByIdAndUpdate with $set on every
  // selected index and, if sendEmail=true, fires a single email per order
  // that contains all products we picked on that order.
  const bulkRefundSubmit = async ({ approve, sendEmail }) => {
    setBulkRefundError('')
    setBulkRefundMessage('')
    if (bulkRefundSelectedRows.length === 0) {
      setBulkRefundError('Select at least one item first.')
      return
    }
    if (!bulkRefundReason) {
      setBulkRefundError('Pick a refund reason — it applies to every selected item.')
      return
    }

    const setBusy = sendEmail ? setBulkRefundSending : setBulkRefundMarking
    setBusy(true)

    const byOrder = new Map()
    for (const row of bulkRefundSelectedRows) {
      const arr = byOrder.get(row.orderId) || []
      arr.push(row.productIndex)
      byOrder.set(row.orderId, arr)
    }

    try {
      const results = []
      for (const [oid, indices] of byOrder.entries()) {
        const payload = {
          indices,
          reason: bulkRefundReason,
          notes: bulkRefundNotes || '',
          approve: !!approve,
          sendEmail: !!sendEmail,
        }
        // eslint-disable-next-line no-await-in-loop
        const { data } = await request.post(`/orders/${oid}/refund-bulk`, payload)
        results.push({ oid, data })

        // Optimistically patch the matching order in the parent. The first
        // call always targets the main order (`order._id`); addons go
        // through `refreshOrders` directly — the parent will re-fetch.
        if (typeof refreshOrders === 'function') {
          if (oid === order._id && data?.order) {
            refreshOrders(data.order)
          }
        }
      }

      // Force a parent refresh so addons + list stay consistent.
      if (typeof refreshOrders === 'function') {
        try { refreshOrders() } catch (_) { /* some callers accept 0 args */ }
      }

      const totalSum = results.reduce((s, r) => s + (Number(r?.data?.total) || 0), 0)
      const actionLabel = sendEmail
        ? (approve ? 'Marked, approved & emailed' : 'Emailed')
        : (approve ? 'Marked & approved' : 'Marked for refund')
      const emailedOk = sendEmail ? results.every(r => r?.data?.emailed) : true
      setBulkRefundMessage(
        `${actionLabel} ${bulkRefundSelectedRows.length} item${bulkRefundSelectedRows.length === 1 ? '' : 's'}` +
        (sendEmail ? ` · total ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalSum)}` : '') +
        (!emailedOk ? ' (email failed — please retry)' : '')
      )
      setBulkRefundSelection(new Set())
      if (!sendEmail) {
        // keep reason/notes so the operator can continue, but clear them on send
      } else {
        setBulkRefundReason('')
        setBulkRefundNotes('')
      }
    } catch (err) {
      console.error('Bulk refund failed:', err)
      setBulkRefundError(err?.response?.data?.error || err?.message || 'Failed to submit refunds')
    } finally {
      setBusy(false)
    }
  }

  const supportNote = `Store Location: ${storeLocation || '-'}\n\nConcierge Arrival Time: ${storeArrivalTime ? storeArrivalTime.toLocaleString() : '-'}\n\nHost Time Window: ${resolvePreferredHostDeliveryTime(order.hostSetDeliveryTime) || '-'}\n\nProperty Address: ${order.property.address?.line1 || '-'}, ${order.property.address?.line2 || '-'},${order.property.address?.city || '-'}\n\nDispatch Notes: ${dispatchNotes || '-'}\n\nTotal Items: ${totalAddedCount}\nGuest Notes: ${guestNote || '-'}\n\nEntry Instructions: ${entryInstructions || order.property.description || '-'}`

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDrawer}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl bg-white rounded-2xl shadow-2xl flex flex-col h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] overflow-hidden">
          
          {/* ═══════════════════════════════════════════════════════════════
              HEADER
              ═══════════════════════════════════════════════════════════════ */}
          <header className="flex-shrink-0 bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Order <span className="font-mono text-sky-600">#{order.uniqueId}</span>
            </h2>

                  {/* Status Badges */}
                  <div className="flex items-center gap-2">
                    {dispatched && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                        <FiCheck className="w-3.5 h-3.5" /> Dispatched
                      </span>
                    )}
                    {needsEntry && !resolvedEntryInstructions && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                        <LockClosedIcon className="w-3.5 h-3.5" /> Needs Entry
                      </span>
                    )}
                    {needsTime && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold">
                        <ClockIcon className="w-3.5 h-3.5" /> Needs Time
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={closeDrawer}
                    className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Quick Info Bar */}
              <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <FiPackage className="w-4 h-4 text-gray-400" />
                  <span className="font-semibold text-gray-900">{totalCount}</span> items
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FiMapPin className="w-4 h-4 text-gray-400" />
                  <span className="truncate max-w-[280px]">{order.property?.address?.city}</span>
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 min-w-0">
                    <FiClock className="w-4 h-4 text-sky-500 shrink-0" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-600 shrink-0">Guest Arrival Time</span>
                    <span className="truncate">{formatDate(order.inStockDate)}</span>
                  </div>
                  {!isDoorDeliveryOrder(order) && order.storeArrivalTime && (
                    <div className="flex items-center gap-2 text-gray-600 min-w-0">
                      <FiClock className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 shrink-0">Concierge Arrival Time</span>
                      <span className="truncate">{formatDateTimePST(order.storeArrivalTime)}</span>
                    </div>
                  )}
                  <HostTimeWindowDisplay hostSetDeliveryTime={order.hostSetDeliveryTime} compact />
                </div>
                {order.storeSpend > 0 && (
                  <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                    <CurrencyDollarIcon className="w-4 h-4" />
                    <span>Spend ${parseFloat(order.storeSpend).toFixed(2)}</span>
                  </div>
              )}
                {parseFloat(order.driverPay) > 0 && (
                  <div className="flex items-center gap-2 text-sky-600 font-semibold">
                    <CurrencyDollarIcon className="w-4 h-4" />
                    <span>Driver ${parseFloat(order.driverPay).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Edit Driver Pay — shown when opened from Awaiting Driver Pay tab */}
          {showEditDriverPay && (
            <div className="flex-shrink-0 px-6 py-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Set concierge pay for this order</h3>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Once set, the order will move to the Assign Driver tab.
                  </p>
                </div>
                <ChangeDeliveryFee
                  entry={order}
                  setMessage={setMessage}
                  driverData={drivers}
                  onSuccess={onDriverPaySet}
                />
              </div>
            </div>
          )}

          {/* Tab navigation and content - Tab.Group wraps both Tab.List and Tab.Panels */}
          {(() => {
            const isDelivery = isDoorDeliveryOrder(order)
            const allTabs = [
              { label: 'Overview', icon: DocumentTextIcon },
              { label: 'Dispatch', icon: TruckIcon },
              { label: 'Products', icon: ShoppingCartIcon },
              { label: 'Refunds', icon: CurrencyDollarIcon },
              { label: 'Activity', icon: PhotoIcon },
              { label: 'Concierge Team', icon: UsersIcon },
            ]
            const tabs = isDelivery ? allTabs.filter(t => t.label !== 'Dispatch') : allTabs
            const selectedIndex = isDelivery ? (activeTab <= 1 ? 0 : activeTab - 1) : activeTab
            const handleTabChange = (idx) => {
              setActiveTab(isDelivery ? (idx === 0 ? 0 : idx + 1) : idx)
            }
            return (
          <Tab.Group selectedIndex={selectedIndex} onChange={handleTabChange} className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Tab Navigation */}
            <Tab.List className="flex px-6 gap-1 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
                  {tabs.map((tab) => (
                    <Tab key={tab.label} className={({ selected }) => `
                      flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all
                      border-b-2 -mb-px focus:outline-none
                      ${selected 
                        ? 'text-sky-600 border-sky-600' 
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                      }
                    `}>
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </Tab>
                  ))}
            </Tab.List>

            {/* ═══════════════════════════════════════════════════════════════
                CONTENT
                ═══════════════════════════════════════════════════════════════ */}
            <Tab.Panels className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                
              {/* ─────────── OVERVIEW TAB ─────────── */}
              <Tab.Panel className="p-6 pb-8 space-y-6 animate-fade-in">
                  {/* Order Summary - Compact inline — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                  <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      {/* Orders */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-md bg-gray-900 text-[10px] font-bold text-white uppercase">Main</span>
                          <span className="font-mono text-sm font-semibold text-gray-900">#{order.uniqueId}</span>
                          <span className="text-xs text-gray-500">
                            ({order.products?.reduce((s, p) => s + (p.quantity || 1), 0) || 0})
                          </span>
                        </div>
                        {addons.map(a => (
                          <div key={a._id} className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-md bg-violet-500 text-[10px] font-bold text-white uppercase">Add-on</span>
                            <span className="font-mono text-sm font-semibold text-gray-900">#{a.uniqueId}</span>
                            <span className="text-xs text-gray-500">
                              ({a.products?.reduce((s, p) => s + (p.quantity || 1), 0) ?? 0})
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* Total */}
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 rounded-lg">
                        <FiPackage className="w-4 h-4 text-sky-600" />
                        <span className="text-sm font-bold text-sky-700">{totalCount} items</span>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Guest & Host Info */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Guest Card */}
                    <div className="bg-gray-50 rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-sky-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Guest</h3>
                          <p className="text-xs text-gray-500">Customer details</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 shrink-0">Email</span>
                          <span className="font-medium text-gray-900 text-right break-all">{order.guestEmail}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 shrink-0">Phone</span>
                          <span className="font-medium text-gray-900">{order.guestPhoneNumber || '–'}</span>
                        </div>
                        {/* AvantStay-only optional names; omitted when guestName is absent */}
                        {getReservationHolderName(order) && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500 shrink-0">Reservation Holder</span>
                            <span className="font-medium text-gray-900 text-right">{getReservationHolderName(order)}</span>
                          </div>
                        )}
                        {getOrderPlacedByName(order) && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500 shrink-0">Order Placed By</span>
                            <span className="font-medium text-gray-900 text-right">{getOrderPlacedByName(order)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Host Card */}
                    <div className="bg-gray-50 rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                          <BuildingOfficeIcon className="w-5 h-5 text-violet-600" />
                        </div>
                  <div>
                          <h3 className="font-semibold text-gray-900">Host</h3>
                          <p className="text-xs text-gray-500">Property manager</p>
                  </div>
              </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 shrink-0">Name</span>
                          <span className="font-medium text-gray-900 text-right">{hostName}</span>
                </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 shrink-0">Email</span>
                          <span className="font-medium text-gray-900 text-right break-all">{host.email || '–'}</span>
                </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 shrink-0">Phone</span>
                          <span className="font-medium text-gray-900">{host.phoneNumber || '–'}</span>
                </div>
                </div>
                </div>
                </div>

                  {/* Guest Arrival Time — from inStockDate */}
                  <div className="relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-400 to-blue-500 rounded-2xl blur opacity-30" />
                    <div className="relative bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-2xl p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-sky-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                          <ClockIcon className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sky-900 text-lg mb-1">Guest Arrival Time</h3>
                          <p className="text-sky-800 font-semibold text-xl">
                            {formatDate(order.inStockDate)}
                          </p>
                          {isDoorDeliveryOrder(order) && (
                            <HostTimeWindowDisplay hostSetDeliveryTime={order.hostSetDeliveryTime} inline inlineBorderClass="border-sky-200/80" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isDoorDeliveryOrder(order) && (
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-2xl blur opacity-30" />
                      <div className="relative bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-5">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                            <ClockIcon className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-emerald-900 text-lg mb-1">Concierge Arrival Time</h3>
                            <p className={`font-semibold text-xl ${order.storeArrivalTime ? 'text-emerald-800' : 'text-emerald-400 italic text-base'}`}>
                              {order.storeArrivalTime ? formatDateTimePST(order.storeArrivalTime) : 'Not set'}
                            </p>
                            <HostTimeWindowDisplay hostSetDeliveryTime={order.hostSetDeliveryTime} inline inlineBorderClass="border-emerald-200/80" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Property Card */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-2xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <MapPinIcon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">Property Address</h3>
                        <p className="text-gray-700">
                          {order.property.address.line1}
                          {order.property.address.line2 && `, ${order.property.address.line2}`}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {order.property.address.city}, {order.property.address.state} {order.property.address.zipCode}
                        </p>
                      </div>
                    </div>
                  </div>

                  {guestAddress && <GuestDeliveryAddressCard data={guestAddress} />}

                  {/* Store Location (editable) */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <MapPinIcon className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-black text-sm mb-1">Store Location</h3>
                        <input
                          type="text"
                          value={storeLocationDisplay}
                          onChange={handleStoreLocationChange}
                          placeholder="Enter store address..."
                          className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Order Summary — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ClipboardDocumentListIcon className="w-5 h-5 text-gray-400" />
                      Order Summary
                    </h3>
                    
                    {/* Main Order */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-1 rounded-lg bg-gray-100 text-xs font-semibold text-gray-600">MAIN</span>
                          <span className="font-mono font-medium">#{order.uniqueId}</span>
                        </div>
                        <span className="text-gray-600">
                          {order.products?.reduce((s, p) => s + (p.quantity || 1), 0) || 0} items
              </span>
                      </div>

                      {/* Add-ons */}
                      {addons.map(a => {
                        const cnt = a.products?.reduce((s, p) => s + (p.quantity || 1), 0) ?? 0
                        return (
                          <div key={a._id} className="flex items-center justify-between py-2 border-t border-gray-100">
                            <div className="flex items-center gap-3">
                              <span className="px-2 py-1 rounded-lg bg-violet-50 text-xs font-semibold text-violet-600">ADD-ON</span>
                              <span className="font-mono font-medium">#{a.uniqueId}</span>
                            </div>
                            <span className="text-gray-600">{cnt} items</span>
                          </div>
                        )
                      })}
                    </div>

                    <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between">
                      <span className="font-semibold text-gray-900">Total Items</span>
                      <span className="font-bold text-lg text-sky-600">{totalCount}</span>
                    </div>
                  </div>
                  )}

                  {/* Financials */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                      <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Store Spend</p>
                      <p className="text-xl font-bold text-emerald-700">
                        {parseFloat(order.storeSpend) > 0 ? `$${parseFloat(order.storeSpend).toFixed(2)}` : '–'}
                      </p>
                    </div>
                    <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
                      <p className="text-xs font-medium text-sky-600 uppercase tracking-wide mb-1">Concierge Pay</p>
                      <p className="text-xl font-bold text-sky-700">
                        {parseFloat(order.driverPay) > 0 ? `$${parseFloat(order.driverPay).toFixed(2)}` : '–'}
                      </p>
                    </div>
                  </div>

                  {/* Delivery Window + (all Dispatch elements in between) + Change property address — only in Overview when delivery */}
                  {isDoorDeliveryOrder(order) && (
                    <>
                      <div className="relative bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-2xl p-5">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                          <ClockIcon className="w-5 h-5 text-sky-500" />
                          Ops Delivery Window
                        </h3>
                        <p className="text-xs text-gray-600 mb-3">Dispatcher time window for delivery (e.g. 7am–11:30PM). Saved to internal notes — separate from the host time window above.</p>
                        <input
                          type="text"
                          value={deliveryWindow}
                          onChange={e => setDeliveryWindow(e.target.value)}
                          placeholder="e.g. 7am–11:30PM"
                          disabled={dispatched}
                          className="w-full bg-white border border-sky-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none disabled:opacity-50"
                        />
                      </div>

                      {/* Add to Kroger Cart — for delivery (Dispatch tab is hidden, so show in Overview) */}
                      {!dispatched && (
                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                          <label className="block font-semibold text-gray-900 mb-3">Add to Kroger Cart</label>
                          {krogerRelinkRequired && (
                            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                              <p className="mb-2">Kroger sign-in is missing or expired. Connect once, then use Add to cart again (you will not need to reconnect until the session expires).</p>
                              <button
                                type="button"
                                onClick={handleConnectKroger}
                                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
                              >
                                Connect Kroger
                              </button>
                            </div>
                          )}
                          {addedCount != null && addedCount > 0 && addedCount === totalAddedCount && (
                            <p className="text-sm font-medium text-emerald-600 mb-3">
                              ✅ All Items Successfully Added to cart
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={handleAddToKrogerCart}
                            disabled={isAdding || (addedCount > 0 && addedCount === totalAddedCount)}
                            className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                          >
                            <ShoppingCartIcon className="w-5 h-5" />
                            {isAdding ? 'Adding...' : 'Add to Cart'}
                          </button>
                        </div>
                      )}

                      {/* Store Spend & Order Confirmation — same as Dispatch */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`bg-white border rounded-2xl p-5 ${refundCount > 0 ? 'border-red-300' : 'border-emerald-300'}`}>
                          <label className="block font-semibold text-gray-900 mb-2">Store Spend</label>
                          <input
                            type="number"
                            step="0.01"
                            value={storeSpend}
                            onChange={e => setStoreSpend(e.target.value)}
                            placeholder="$0.00"
                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none font-mono"
                          />
                          <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                            <span className="text-3xl font-bold text-sky-600">{totalCount}</span>
                            <span className="text-lg text-gray-500 ml-2">items</span>
                          </div>
                          {refundCount > 0 ? (
                            <div className="mt-3 pt-3 border-t border-red-100">
                              <div className="bg-red-50 rounded-xl p-3">
                                <p className="text-sm text-red-700 font-medium mb-1">
                                  If out of stock these items need to be refunded
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRefundFilterActive(true)
                                    setActiveTab(2)
                                  }}
                                  className="text-sm text-red-600 font-semibold underline hover:text-red-800 transition-colors"
                                >
                                  View {refundCount} refund item{refundCount !== 1 ? 's' : ''} →
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 pt-3 border-t border-emerald-100">
                              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                                <span className="text-sm text-emerald-700 font-medium">
                                  ✓ Substitute any item if out of stock
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="relative rounded-2xl overflow-hidden">
                          <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 rounded-2xl blur opacity-30" aria-hidden />
                          <div className="relative bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-2xl p-5">
                            <label className="block font-semibold mb-2 text-sky-900">
                              Order Confirmation URL
                              <span className="text-xs font-normal text-gray-500 ml-2">(paste store order link)</span>
                            </label>
                            <div className="mb-3">
                              <label className="block text-xs text-gray-500 mb-1">Store</label>
                              <select
                                value={company}
                                onChange={e => setCompany(e.target.value)}
                                disabled={dispatched}
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none disabled:opacity-50"
                              >
                                {Object.keys(STORE_CONFIGS).map(storeName => (
                                  <option key={storeName} value={storeName}>{storeName}</option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="url"
                              value={storeOrderConfirmationUrl}
                              onChange={e => {
                                const value = e.target.value
                                setStoreOrderConfirmationUrl(value)
                                const detectedCompany = detectCompanyFromUrl(value)
                                if (detectedCompany) setCompany(detectedCompany)
                                setUrlError(value && !isValidUrl(value) ? 'Please enter a valid URL' : null)
                              }}
                              placeholder="https://www.ralphs.com/mypurchases/pending/..."
                              disabled={dispatched}
                              className={`w-full bg-gray-50 border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:outline-none disabled:opacity-50 ${urlError ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-sky-500 focus:ring-sky-500/20'}`}
                            />
                            {urlError && <p className="text-xs text-red-600 mt-1">{urlError}</p>}
                            {extractedOrderNumber && (
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-xs text-gray-500">Order #:</span>
                                <span className="font-mono text-sm font-medium text-gray-900">{extractedOrderNumber}</span>
                                <a
                                  href={generatedOrderUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-lg transition-colors"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                  </svg>
                                  View on {company}
                                </a>
                              </div>
                            )}
                            {order.storeOrderId && !storeOrderConfirmationUrl && (
                              <p className="text-xs text-gray-500 mt-2">
                                Legacy ID: <span className="font-mono">{order.storeOrderId}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Entry Instructions — same as Dispatch */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <LockClosedIcon className="w-5 h-5 text-gray-400" />
                            Entry Instructions
                          </h3>
                          <LockboxDetailsLauncher
                            orderId={order?._id}
                            propertyLabel={propertyAccessLabel}
                            propertyId={order?.propertyID}
                          />
                        </div>
                        {resolvedEntryInstructions && entryInstructionsLastUpdatedLabel && (
                          <p className="mb-2 text-sm font-bold text-emerald-600">
                            Last Updated: {entryInstructionsLastUpdatedLabel}
                          </p>
                        )}
                        <textarea
                          rows={8}
                          value={entryInstructions}
                          onChange={e => setEntryInstructions(e.target.value)}
                          placeholder={order.property.description || 'E.g. keypad code is 1234, park in spot #7...'}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none resize-none"
                        />
                        {entryHistory.length > 0 && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => setShowHistory(h => !h)}
                              className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                              View history ({entryHistory.length})
                            </button>
                            <Transition
                              show={showHistory}
                              enter="transition duration-150"
                              enterFrom="opacity-0 -translate-y-2"
                              enterTo="opacity-100 translate-y-0"
                              leave="transition duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                                {entryHistory.map((h, i) => (
                                  <div key={i} className="bg-gray-100 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 mb-1">{format(h.date, 'PP p')}</p>
                                    <p className="text-sm text-gray-700">{h.text}</p>
                                  </div>
                                ))}
                              </div>
                            </Transition>
                          </div>
                        )}
                      </div>

                      {/* Internal Notes — same as Dispatch */}
                      <div className="bg-coral-50 border border-coral-100 rounded-2xl p-5">
                        <h3 className="font-semibold text-coral-900 mb-2">Internal Notes <span className="text-coral-600">(Bellhopt-only)</span></h3>
                        <p className="text-xs text-coral-700/70 mb-4">Visible to Bellhopt staff only. Do not include anything meant for the Concierge Team or guests.</p>
                        <textarea
                          rows={3}
                          value={internalNotes}
                          onChange={e => setInternalNotes(e.target.value)}
                          placeholder="Include info the staff needs to know..."
                          className="w-full bg-white border border-coral-200 rounded-xl px-4 py-3 text-sm focus:border-coral-500 focus:ring-2 focus:ring-coral-500/20 focus:outline-none resize-none"
                        />
                      </div>

                      {/* Change property address */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block font-semibold text-gray-900">Change property address</label>
                          <button
                            type="button"
                            onClick={() => setPropertyUnlocked(prev => !prev)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              propertyUnlocked 
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {propertyUnlocked ? (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                                </svg>
                                Unlocked
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                                </svg>
                                Locked
                              </>
                            )}
                          </button>
                        </div>
                        <div className={`${!propertyUnlocked ? 'opacity-50 pointer-events-none' : ''}`}>
                          <SelectDropdown
                            value={selectedProperty}
                            options={propertyOptions}
                            isMulti={false}
                            onChange={setSelectedProperty}
                            isDisabled={!propertyUnlocked}
                            placeholder="Search by street, city, state, or zip…"
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          />
                        </div>
                        {!propertyUnlocked && (
                          <p className="text-xs text-gray-500 mt-2">Click "Locked" to enable editing</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Predicted Pay - Commented out
                  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-emerald-900">Suggested Concierge Pay</h3>
                        <p className="text-sm text-emerald-700/70">Based on distance and order size</p>
                      </div>
                      <div className="text-right">
                {predictedPay != null ? (
                          <span className="text-3xl font-bold text-emerald-600">${predictedPay.toFixed(2)}</span>
                ) : payError ? (
                          <span className="text-coral-600 font-medium">{payError}</span>
                ) : (
                          <span className="text-emerald-600">Loading...</span>
                )}
                      </div>
              </div>
            </div>
                  */}

                  {/* Cleaning Report — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                  <CleaningReportPanel order={order} />
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
              <button
                      onClick={handleResendConfirmation}
                      className="flex-1 py-3 px-4 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      <PaperAirplaneIcon className="w-4 h-4" />
                      Send Guest Receipt
              </button>
                  </div>

                {confirmMessage && <p className="text-sm text-emerald-600 text-center">{confirmMessage}</p>}
                {confirmError && <p className="text-sm text-coral-600 text-center">{confirmError}</p>}
              </Tab.Panel>

              {/* ─────────── DISPATCH TAB ─────────── (hidden for delivery) */}
              {!isDoorDeliveryOrder(order) && (
              <Tab.Panel className="p-6 pb-8 space-y-6 animate-fade-in">
                  {/* Host Notes Alert - Vibrant with glow */}
                  {hostNotes && (
                    <div className="relative">
                      {/* Glow effect */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 rounded-2xl blur-sm opacity-75 animate-pulse" />
                      <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 shadow-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-bold text-white flex items-center gap-2 text-base">
                              <ExclamationTriangleIcon className="w-6 h-6" />
                              ⚠️ IMPORTANT HOST NOTES
                            </h4>
                            <p className="text-sm text-white/90 mt-2 whitespace-pre-wrap font-medium">{hostNotes}</p>
                          </div>
                          <button
                            onClick={handleAddHostNotes}
                            className="px-4 py-2 text-sm font-bold rounded-lg bg-white text-amber-600 hover:bg-amber-50 transition-colors whitespace-nowrap shadow-md"
                          >
                            Add to Dispatch
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delivery Type Alert */}
                  {order.deliveryFeeType && (
                    <div className="relative">
                      {/* Glow effect */}
                      <div className={`absolute -inset-1 rounded-2xl blur-sm opacity-75 ${
                        isDoorDeliveryOrder(order) 
                          ? 'bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500' 
                          : 'bg-gradient-to-r from-violet-400 via-purple-500 to-fuchsia-500'
                      }`} />
                      <div className={`relative rounded-xl p-4 shadow-lg ${
                        isDoorDeliveryOrder(order)
                          ? 'bg-gradient-to-r from-sky-500 to-blue-600'
                          : 'bg-gradient-to-r from-violet-500 to-purple-600'
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isDoorDeliveryOrder(order)
                              ? 'bg-white/20'
                              : 'bg-white/20'
                          }`}>
                            {isDoorDeliveryOrder(order) ? (
                              <TruckIcon className="w-7 h-7 text-white" />
                            ) : (
                              <FiPackage className="w-7 h-7 text-white" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-bold text-white text-lg tracking-wide">
                              {isDoorDeliveryOrder(order) 
                                ? '🚚 DELIVERY' 
                                : '📦 STOCK PRE-ARRIVAL'}
                            </h4>
                            <p className="text-sm text-white/80 mt-0.5">
                              {isDoorDeliveryOrder(order)
                                ? 'Deliver items directly to guest at the door'
                                : 'Stock items in property before guest arrives'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add to Kroger Cart - directly after delivery type */}
                  {!dispatched && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-5">
                      <label className="block font-semibold text-gray-900 mb-3">Add to Kroger Cart</label>
                      {krogerRelinkRequired && (
                        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                          <p className="mb-2">Kroger sign-in is missing or expired. Connect once, then use Add to cart again (you will not need to reconnect until the session expires).</p>
                          <button
                            type="button"
                            onClick={handleConnectKroger}
                            className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
                          >
                            Connect Kroger
                          </button>
                        </div>
                      )}
                      {addedCount != null && addedCount > 0 && addedCount === totalAddedCount && (
                        <p className="text-sm font-medium text-emerald-600 mb-3">
                          ✅ All Items Successfully Added to cart
                        </p>
                      )}
                      <button
                        onClick={handleAddToKrogerCart}
                        disabled={isAdding || (addedCount > 0 && addedCount === totalAddedCount)}
                        className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        <ShoppingCartIcon className="w-5 h-5" />
                        {isAdding ? 'Adding...' : 'Add to Cart'}
                      </button>
                    </div>
                  )}

                  {guestAddress && <GuestDeliveryAddressCard data={guestAddress} />}

                  {/* Store Location - above Store Arrival Time */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h3 className="font-semibold text-black flex items-center gap-2 mb-4">
                      <MapPinIcon className="w-5 h-5 text-gray-400" />
                Store Location
                    </h3>
              <input
                type="text"
                value={storeLocationDisplay}
                onChange={handleStoreLocationChange}
                      placeholder="Enter full store address"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none"
              />
                    {isDoorDeliveryOrder(order) && order.property?.address && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Property Address (delivery)</p>
                        <p className="text-sm text-gray-900 font-medium">
                          {order.property.address.line1}
                          {order.property.address.line2 && `, ${order.property.address.line2}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.property.address.city}, {order.property.address.state} {order.property.address.zipCode}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Store Arrival Time — hidden for delivery; delivery uses Delivery Window instead */}
                  {!isDoorDeliveryOrder(order) && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-emerald-500" />
                        Concierge Arrival Time
                      </h3>
                      <label className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Allow date change</span>
                <button
                  type="button"
                          onClick={handleAllowDateToggle}
                          className={`relative w-10 h-6 rounded-full transition-colors ${allowDateChange ? 'bg-sky-500' : 'bg-gray-200'}`}
                >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${allowDateChange ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
                      </label>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                When the concierge should arrive at the store — separate from guest arrival time at the property.
              </p>

              <DatePicker
                selected={storeArrivalTime}
                onChange={handleStoreArrivalTimeChange}
                showTimeSelect
                showTimeSelectOnly={!allowDateChange}
                dateFormat={allowDateChange ? 'Pp' : 'h:mm aa'}
                minDate={allowDateChange ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })() : new Date(order.inStockDate)}
                placeholderText="12:30 PM"
                      className={`w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none ${dateChanged ? 'text-coral-600 font-bold border-coral-300' : ''}`}
                    />

              <HostTimeWindowDisplay hostSetDeliveryTime={order.hostSetDeliveryTime} inline />

              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <ClockIcon className="w-4 h-4 text-sky-500 shrink-0" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-600 shrink-0">Guest Arrival Time</span>
                  <span className="text-gray-700 truncate">{formatDate(order.inStockDate)}</span>
                </div>
                {order.storeArrivalTime && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <ClockIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 shrink-0">Saved Concierge Arrival Time</span>
                    <span className="text-gray-700 truncate">{formatDateTimePST(order.storeArrivalTime)}</span>
                  </div>
                )}
              </div>

              {dateChanged && (
                      <p className="text-coral-600 text-sm font-medium mt-2 flex items-center gap-1">
                        <ExclamationTriangleIcon className="w-4 h-4" />
                        Delivery date has changed from original!
                </p>
              )}
                  </div>
                  )}

                  {/* Delivery Window — only for delivery; appended to internal notes on save */}
                  {isDoorDeliveryOrder(order) && (
                  <>
                  <div className="relative rounded-2xl overflow-hidden">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 rounded-2xl blur opacity-30" />
                    <div className="relative bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-2xl p-5">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                        <ClockIcon className="w-5 h-5 text-sky-500" />
                        Ops Delivery Window
                      </h3>
                      <p className="text-xs text-gray-600 mb-3">Dispatcher time window for delivery (e.g. 7am–11:30PM). Saved to internal notes — separate from the host time window above.</p>
                      <input
                        type="text"
                        value={deliveryWindow}
                        onChange={e => setDeliveryWindow(e.target.value)}
                        placeholder="e.g. 7am–11:30PM"
                        disabled={dispatched}
                        className="w-full bg-white border border-sky-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none disabled:opacity-50"
                      />
                    </div>
                  </div>
                  </>
                  )}

                  {/* Store Spend & Order Confirmation */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`bg-white border rounded-2xl p-5 ${refundCount > 0 ? 'border-red-300' : 'border-emerald-300'}`}>
                      <label className="block font-semibold text-gray-900 mb-2">Store Spend</label>
                      <input
                        type="number"
                        step="0.01"
                        value={storeSpend}
                        onChange={e => setStoreSpend(e.target.value)}
                        placeholder="$0.00"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none font-mono"
                      />
                      <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                        <span className="text-3xl font-bold text-sky-600">{totalCount}</span>
                        <span className="text-lg text-gray-500 ml-2">items</span>
                      </div>
                      {refundCount > 0 ? (
                        <div className="mt-3 pt-3 border-t border-red-100">
                          <div className="bg-red-50 rounded-xl p-3">
                            <p className="text-sm text-red-700 font-medium mb-1">
                              If out of stock these items need to be refunded
                            </p>
                            <button
                              onClick={() => {
                                setRefundFilterActive(true)
                                setActiveTab(2) // Products tab
                              }}
                              className="text-sm text-red-600 font-semibold underline hover:text-red-800 transition-colors"
                            >
                              View {refundCount} refund item{refundCount !== 1 ? 's' : ''} →
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 pt-3 border-t border-emerald-100">
                          <div className="bg-emerald-50 rounded-xl p-3 text-center">
                            <span className="text-sm text-emerald-700 font-medium">
                              ✓ Substitute any item if out of stock
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={isDoorDeliveryOrder(order) ? 'relative rounded-2xl overflow-hidden' : ''}>
                      {isDoorDeliveryOrder(order) && (
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 rounded-2xl blur opacity-30" aria-hidden />
                      )}
                      <div className={isDoorDeliveryOrder(order)
                        ? 'relative bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-2xl p-5'
                        : 'bg-white border border-gray-200 rounded-2xl p-5'
                      }>
                      <label className={`block font-semibold mb-2 ${isDoorDeliveryOrder(order) ? 'text-sky-900' : 'text-gray-900'}`}>
                        Order Confirmation URL
                        <span className="text-xs font-normal text-gray-500 ml-2">(paste store order link)</span>
                      </label>
                      
                      {/* Store Dropdown */}
                      <div className="mb-3">
                        <label className="block text-xs text-gray-500 mb-1">Store</label>
                        <select
                          value={company}
                          onChange={e => setCompany(e.target.value)}
                          disabled={dispatched}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none disabled:opacity-50"
                        >
                          {Object.keys(STORE_CONFIGS).map(storeName => (
                            <option key={storeName} value={storeName}>{storeName}</option>
                          ))}
                        </select>
                      </div>

                      {/* Order URL Input */}
                      <input
                        type="url"
                        value={storeOrderConfirmationUrl}
                        onChange={e => {
                          const value = e.target.value;
                          setStoreOrderConfirmationUrl(value);
                          
                          // Auto-detect company from URL
                          const detectedCompany = detectCompanyFromUrl(value);
                          if (detectedCompany) {
                            setCompany(detectedCompany);
                          }
                          
                          // Validate URL
                          setUrlError(value && !isValidUrl(value) ? 'Please enter a valid URL' : null);
                        }}
                        placeholder="https://www.ralphs.com/mypurchases/pending/..."
                        disabled={dispatched}
                        className={`w-full bg-gray-50 border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:outline-none disabled:opacity-50 ${
                          urlError ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-sky-500 focus:ring-sky-500/20'
                        }`}
                      />
                      {urlError && (
                        <p className="text-xs text-red-600 mt-1">{urlError}</p>
                      )}

                      {/* Extracted Order Number & Link Button */}
                      {extractedOrderNumber && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Order #:</span>
                          <span className="font-mono text-sm font-medium text-gray-900">{extractedOrderNumber}</span>
                          <a
                            href={generatedOrderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                            View on {company}
                          </a>
                        </div>
                      )}

                      {/* Legacy field - hidden but still functional for old orders */}
                      {order.storeOrderId && !storeOrderConfirmationUrl && (
                        <p className="text-xs text-gray-500 mt-2">
                          Legacy ID: <span className="font-mono">{order.storeOrderId}</span>
                        </p>
                      )}

                      {/* Grocery Store Order Name — hidden for delivery */}
                      {!isDoorDeliveryOrder(order) && (
                      <div className="mt-3">
                        <label className="block text-xs text-gray-500 mb-1">Grocery Store Order Name</label>
                        <input
                          type="text"
                          value={groceryOrderName}
                          onChange={e => {
                            const newName = e.target.value
                            setGroceryOrderName(newName)
                            setDispatchNotes(prev => {
                              let cleaned = prev.replace(/\n*Grocery Store Order Name:.*$/m, '').trim()
                              if (newName.trim()) {
                                return `${cleaned}\n\nGrocery Store Order Name: ${newName.trim()}`.trim()
                              }
                              return cleaned
                            })
                          }}
                          placeholder={order.guestEmail ? `Hint: ${order.guestEmail}` : 'Enter order name (appended to dispatch notes)'}
                          disabled={dispatched}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none disabled:opacity-50"
                        />
                      </div>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Flags / Entry Instructions Activity — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                    host.receiveEntryInstructionsEmails ? (
                      <div className="bg-white border border-gray-200 rounded-2xl p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                          <PaperAirplaneIcon className="w-4 h-4 text-sky-500" />
                          Entry Instructions Email Activity
                        </h3>
                        <div className="relative pl-5">
                          {/* vertical timeline line */}
                          <div className="absolute left-[7px] top-1 bottom-1 w-0.5 bg-gray-200" />

                          {/* Step 1: Initial Email Sent */}
                          <div className="relative flex items-start gap-3 pb-3">
                            <div className={`relative z-10 mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${order.entryInstructionsEmailSent ? 'border-sky-500 bg-sky-500' : 'border-gray-300 bg-white'}`}>
                              {order.entryInstructionsEmailSent && (
                                <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-xs font-medium ${order.entryInstructionsEmailSent ? 'text-gray-900' : 'text-gray-400'}`}>
                                Initial Email Sent
                              </p>
                              {order.entryInstructionsEmailSentAt && (
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                  {new Date(order.entryInstructionsEmailSentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </p>
                              )}
                              {!order.entryInstructionsEmailSent && (
                                <p className="text-[11px] text-gray-400 mt-0.5">Pending</p>
                              )}
                            </div>
                          </div>

                          {/* Step 2: Follow-Up Emails */}
                          {order.entryInstructionsFollowUpSent && order.entryInstructionsFollowUpSent.length > 0 ? (
                            order.entryInstructionsFollowUpSent
                              .slice()
                              .sort((a, b) => b - a)
                              .map((day) => (
                                <div key={day} className="relative flex items-start gap-3 pb-3">
                                  <div className="relative z-10 mt-0.5 w-3.5 h-3.5 rounded-full border-2 border-amber-500 bg-amber-500 flex items-center justify-center shrink-0">
                                    <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-900">
                                      Follow-Up Sent &middot; {day} day{day !== 1 ? 's' : ''} before delivery
                                    </p>
                                  </div>
                                </div>
                              ))
                          ) : order.entryInstructionsEmailSent ? (
                            <div className="relative flex items-start gap-3 pb-3">
                              <div className="relative z-10 mt-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-400">No Follow-Ups Sent Yet</p>
                              </div>
                            </div>
                          ) : null}

                          {/* Step 3: Host Updated Entry Instructions */}
                          <div className="relative flex items-start gap-3">
                            <div className={`relative z-10 mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${order.hostOrderDetailsInputted ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300 bg-white'}`}>
                              {order.hostOrderDetailsInputted && (
                                <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              {order.hostOrderDetailsInputted ? (
                                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Entry Instructions Updated</p>
                              ) : (
                                <p className="text-xs font-medium text-gray-400">Awaiting Host Response</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-2xl p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm">Quick Flags</h3>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1">
                            <input
                              type="checkbox"
                              checked={needsEntry}
                              onChange={e => setNeedsEntry(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                            />
                            <div>
                              <p className="font-medium text-gray-900 text-xs">Needs Entry Instructions</p>
                            </div>
                          </label>
                          <label className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1">
                            <input
                              type="checkbox"
                              checked={needsTime}
                              onChange={e => setNeedsTime(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                            />
                            <div>
                              <p className="font-medium text-gray-900 text-xs">Needs Delivery Time</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    )
                  )}

                  {/* Entry Instructions */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <LockClosedIcon className="w-5 h-5 text-gray-400" />
                        Entry Instructions
              </h3>
              <LockboxDetailsLauncher
                orderId={order?._id}
                propertyLabel={propertyAccessLabel}
                propertyId={order?.propertyID}
              />
            </div>
                        {resolvedEntryInstructions && entryInstructionsLastUpdatedLabel && (
                          <p className="mb-2 text-sm font-bold text-emerald-600">
                            Last Updated: {entryInstructionsLastUpdatedLabel}
                          </p>
                        )}
              <textarea
                      rows={8}
                value={entryInstructions}
                onChange={e => setEntryInstructions(e.target.value)}
                      placeholder={order.property.description || 'E.g. keypad code is 1234, park in spot #7...'}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none resize-none"
              />

                    {/* History */}
            {entryHistory.length > 0 && (
                      <div className="mt-4">
                <button
                  onClick={() => setShowHistory(h => !h)}
                          className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          <ChevronDownIcon className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                          View history ({entryHistory.length})
                </button>

                <Transition
                  show={showHistory}
                          enter="transition duration-150"
                          enterFrom="opacity-0 -translate-y-2"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                >
                          <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                    {entryHistory.map((h, i) => (
                              <div key={i} className="bg-gray-100 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">{format(h.date, 'PP p')}</p>
                                <p className="text-sm text-gray-700">{h.text}</p>
                      </div>
                    ))}
                  </div>
                </Transition>
                      </div>
            )}
                  </div>

                  {/* Dispatch Notes — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Extra Dispatch Notes</h3>
              <textarea
                      rows={4}
                value={dispatchNotes}
                onChange={e => setDispatchNotes(e.target.value)}
                      placeholder="Type any special instructions for the driver..."
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none resize-none"
              />

                    {guestNote && (
                      <div className="mt-3 bg-sky-50 border-l-4 border-sky-400 rounded-r-xl p-3">
                        <p className="text-sm font-semibold text-sky-700 mb-1">Guest Notes (auto-included):</p>
                        <p className="text-sm text-gray-700">{guestNote}</p>
                        <p className="text-xs text-gray-500 mt-2 italic">These notes will be appended automatically when dispatched.</p>
                </div>
              )}
                  </div>
                  )}

                  {/* Internal Notes */}
                  <div className="bg-coral-50 border border-coral-100 rounded-2xl p-5">
                    <h3 className="font-semibold text-coral-900 mb-2">Internal Notes <span className="text-coral-600">(Bellhopt-only)</span></h3>
                    <p className="text-xs text-coral-700/70 mb-4">Visible to Bellhopt staff only. Do not include anything meant for the Concierge Team or guests.</p>
              <textarea
                      rows={3}
                value={internalNotes}
                onChange={e => setInternalNotes(e.target.value)}
                      placeholder="Include info the staff needs to know..."
                      className="w-full bg-white border border-coral-200 rounded-xl px-4 py-3 text-sm focus:border-coral-500 focus:ring-2 focus:ring-coral-500/20 focus:outline-none resize-none"
              />
                  </div>

                  {/* Change Property Address */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold text-gray-900">Change property address</label>
                      <button
                        type="button"
                        onClick={() => setPropertyUnlocked(prev => !prev)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          propertyUnlocked 
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {propertyUnlocked ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                            </svg>
                            Unlocked
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                            </svg>
                            Locked
                          </>
                        )}
                      </button>
                    </div>
                    <div className={`${!propertyUnlocked ? 'opacity-50 pointer-events-none' : ''}`}>
                      <SelectDropdown
                        value={selectedProperty}
                        options={propertyOptions}
                        isMulti={false}
                        onChange={setSelectedProperty}
                        isDisabled={!propertyUnlocked}
                        placeholder="Search by street, city, state, or zip…"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    </div>
                    {!propertyUnlocked && (
                      <p className="text-xs text-gray-500 mt-2">Click "Locked" to enable editing</p>
                    )}
                  </div>

                  {/* Suggested Drivers — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && suggestedDrivers.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-5">
                      <button
                        onClick={() => setOpenHistory(o => !o)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3 className="font-semibold text-gray-900">Suggested Concierges (≤ 20 mi)</h3>
                        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${openHistory ? 'rotate-180' : ''}`} />
                      </button>
                      
                      <Transition
                        show={openHistory}
                        enter="transition duration-150"
                        enterFrom="opacity-0 -translate-y-2"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <ul className="mt-4 space-y-2">
                          {suggestedDrivers.map(d => (
                            <li key={d._id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="font-medium text-gray-900">{d.name}</span>
                              <span className="text-sm text-gray-500">{d.distance.toFixed(1)} mi</span>
                            </li>
                          ))}
                        </ul>
                      </Transition>

                      {showBroadcast && (
                        <button
                          onClick={() => setBroadcastOpen(true)}
                          className="mt-4 w-full py-2.5 bg-sky-500 hover:bg-sky-600 text-white font-medium rounded-xl transition-colors"
                        >
                          Ask Suggested Drivers ({suggestedDrivers.length})
                        </button>
                      )}
                    </div>
                  )}

                  {/* Support Notes (collapsible) — hidden for delivery */}
                  {!isDoorDeliveryOrder(order) && (
                  <details className="bg-gray-50 rounded-2xl overflow-hidden">
                    <summary className="px-5 py-4 cursor-pointer font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
                      Support Notes (Copy-ready)
                    </summary>
                    <div className="px-5 pb-5">
              <textarea
                readOnly
                        rows={8}
                value={supportNote}
                        className="w-full bg-white border border-gray-200 rounded-xl p-4 text-xs font-mono text-gray-600 resize-none"
              />
                    </div>
                </details>
                  )}
              </Tab.Panel>
              )}

              {/* ─────────── PRODUCTS TAB ─────────── */}
              <Tab.Panel className="p-6 pb-8 animate-fade-in">
                  {/* Refund launcher — points operators at the new Refunds tab
                      where multi-select / preview / send all happen in one place. */}
                  {refundLineItems.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
                      <div>
                        <p className="text-sm font-semibold text-red-700">
                          {refundLineItems.length} item{refundLineItems.length !== 1 ? 's' : ''} marked for refund
                        </p>
                        <p className="mt-0.5 text-xs text-red-600/80">
                          Handle refunds in the Refunds tab — pick items, preview the email, send in one click.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab(3)}
                        className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
                      >
                        Open Refunds tab →
                      </button>
                    </div>
                  )}

                  {/* Filter Banner */}
                  {refundFilterActive && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-red-500">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                        <span className="text-sm font-medium text-red-700">Showing only items marked for refund</span>
                      </div>
                      <button
                        onClick={() => setRefundFilterActive(false)}
                        className="text-sm text-red-600 hover:text-red-800 font-medium underline transition-colors"
                      >
                        Show all products
                      </button>
                    </div>
                  )}

                  <div className="space-y-6">
                    {[order, ...addons].map(o => {
                      // Filter products if refund filter is active
                      const displayProducts = refundFilterActive 
                        ? (o.products || []).filter(p => p.substituteOrRefund === 'refund')
                        : (o.products || []);
                      
                      // Skip orders with no matching products when filter is active
                      if (refundFilterActive && displayProducts.length === 0) return null;
                      
                      return (
                        <div key={o.uniqueId}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${o._isAddon ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-700'}`}>
                                {o._isAddon ? 'ADD-ON' : 'MAIN ORDER'}
                              </span>
                              <span className="font-mono font-semibold text-gray-900">#{o.uniqueId}</span>
                              <span className="text-sm text-gray-500">
                                {displayProducts.reduce((s, p) => s + (p.quantity || 1), 0)} items
                                {refundFilterActive && ` (filtered)`}
                              </span>
                            </div>
                            
                            {/* Refund Stats for this order */}
                            {!refundFilterActive && (o.products || []).some(p => p.refunded) && (
                              <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-lg">
                                {(o.products || []).filter(p => p.refunded).length} item(s) marked for refund
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            {displayProducts.map((p, idx) => (
                              <ProductCardsDispatch
                                key={p._id || idx}
                                product={p}
                                productIndex={idx}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
              </Tab.Panel>

              {/* ─────────── REFUNDS TAB ─────────── */}
              <Tab.Panel className="p-6 pb-8 animate-fade-in">
                <RefundsTab
                  order={order}
                  guestName={getOrderGuestDisplayName(order, 'there')}
                  rows={bulkRefundRows}
                  selection={bulkRefundSelection}
                  selectedRows={bulkRefundSelectedRows}
                  previewGroups={bulkRefundPreviewGroups}
                  previewByOrder={bulkRefundPreviewByOrder}
                  previewLoading={bulkRefundPreviewLoading}
                  previewError={bulkRefundPreviewError}
                  total={bulkRefundTotal}
                  reason={bulkRefundReason}
                  setReason={setBulkRefundReason}
                  notes={bulkRefundNotes}
                  setNotes={setBulkRefundNotes}
                  onToggle={bulkRefundToggle}
                  onSelectAll={bulkRefundSelectAll}
                  onSelectOutOfStock={bulkRefundSelectOutOfStock}
                  onClear={bulkRefundClear}
                  sending={bulkRefundSending}
                  marking={bulkRefundMarking}
                  error={bulkRefundError}
                  message={bulkRefundMessage}
                  onMark={() => bulkRefundSubmit({ approve: false, sendEmail: false })}
                  onSend={() => bulkRefundSubmit({ approve: true, sendEmail: true })}
                />
              </Tab.Panel>

              {/* ─────────── ACTIVITY TAB ─────────── */}
              <Tab.Panel className="p-6 pb-8 animate-fade-in">
                  {/* Proof Images */}
                  {imageUrls.length > 0 ? (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <PhotoIcon className="w-5 h-5 text-gray-400" />
                        Delivery Photos ({imageUrls.length})
                      </h3>
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    {imageUrls.map((url, idx) => (
      <img
        key={idx}
        src={typeof url === 'string' ? url : url.url}
                            alt={`Delivery proof ${idx + 1}`}
                            className="w-full aspect-square object-cover rounded-2xl shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
      />
    ))}
  </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
                        <PhotoIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">No photos yet</h3>
                      <p className="text-gray-500 text-sm">Delivery photos will appear here once uploaded</p>
                  </div>
                )}
              </Tab.Panel>

              {/* ─────────── DRIVERS TAB ─────────── */}
              <Tab.Panel className="p-6 pb-8 animate-fade-in">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <UsersIcon className="w-5 h-5 text-gray-400" />
                      Suggested Drivers
                    </h3>
                    <span className="text-sm text-gray-500">
                      {suggestedDrivers.length} drivers within 20 mi
                    </span>
                  </div>

                  {suggestedDrivers.length > 0 ? (
                    <div className="space-y-3">
                      {/* Sort by order count descending */}
                      {[...suggestedDrivers]
                        .sort((a, b) => (b.orderCount || 0) - (a.orderCount || 0))
                        .map((driver, idx) => (
                          <div 
                            key={driver._id} 
                            className="bg-white border border-gray-200 rounded-xl p-4 hover:border-sky-300 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                  idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-gray-300'
                                }`}>
                                  {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : (idx + 1)}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{driver.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <MapPinIcon className="w-3 h-3" />
                                      {driver.distance?.toFixed(1)} mi away
                                    </span>
                                    {driver.currentAddress?.city && (
                                      <>
                                        <span>•</span>
                                        <span>{driver.currentAddress.city}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="flex items-center gap-1 text-emerald-600 font-semibold">
                                  <FiPackage className="w-4 h-4" />
                                  <span>{driver.orderCount || 0}</span>
                                </div>
                                <p className="text-xs text-gray-500">orders</p>
                              </div>
                            </div>
                            
                            {/* Last Order Info */}
                            {driver.lastOrderDate && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Last order:</span>
                                  <span className="text-gray-700">
                                    {new Date(driver.lastOrderDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                {driver.lastOrderAddress && (
                                  <p className="text-xs text-gray-400 mt-1 truncate">
                                    {driver.lastOrderAddress}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
                        <UsersIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">No nearby concierges</h3>
                      <p className="text-gray-500 text-sm">No concierges found within 20 miles of this property</p>
                    </div>
                  )}

                  {/* Broadcast Button */}
                  {suggestedDrivers.length > 0 && showBroadcast && (
                    <button
                      onClick={() => setBroadcastOpen(true)}
                      className="w-full py-3 bg-sky-500 hover:bg-sky-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      <PaperAirplaneIcon className="w-5 h-5" />
                      Broadcast to {suggestedDrivers.length} Drivers
                    </button>
                  )}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
            )
          })()}

          {/* ═══════════════════════════════════════════════════════════════
              FOOTER
              ═══════════════════════════════════════════════════════════════ */}
          <footer className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
            {/* Messages */}
            {(error || message) && (
              <div className="mb-4">
                {error && <p className="text-sm text-coral-600 bg-coral-50 rounded-lg px-3 py-2">{error}</p>}
                {message && <p className="text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">{message}</p>}
          </div>
            )}

            {!dispatched && !hasStoreOrderReference && (
              <p className="mb-3 text-xs text-gray-500">
                Saves your edits but keeps this order in Awaiting Dispatch — paste the Order Confirmation URL to move it forward.
              </p>
            )}

            <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
                className="flex-1 py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl transition-colors"
            >
              Save Changes
            </button>

          <button
            onClick={closeDrawer}
                className="py-3 px-6 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
          </footer>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {isOpen ? (
        <>
        {/* Chat Panel removed — no chat overlay from the order modal. */}
        {/* Legacy single-product refund modal removed — use the Refunds tab. */}
        </>
      ) : null}
    </>
  )
}

// ─────────────────────────────────────────────────────────────
// Refunds tab — multi-select refund flow with live email preview.
//
// One shared Reason + Notes applies to every checked product. "Mark N for
// refund" flags items only (so an approver can finish later). "Send refund
// email" marks + auto-approves (drops into RefundOpsQueue) and sends ONE
// email that lists every selected product with the combined total — across
// main + addon orders it calls the backend once per underlying order, but
// the guest still gets one email per order (each listing all picked items
// from that order). Already-refunded items are shown read-only as history.
// ─────────────────────────────────────────────────────────────

const REFUND_REASONS = [
  { value: '', label: 'Select reason…' },
  { value: 'outOfStock', label: 'Out of Stock' },
  { value: 'damaged', label: 'Damaged / Poor Quality' },
  { value: 'wrongItem', label: 'Wrong Item' },
  { value: 'expired', label: 'Expired / Near Expiry' },
  { value: 'substitutionRejected', label: 'Substitution Rejected' },
  { value: 'other', label: 'Other' },
]

function currency(n) {
  const v = Number(n) || 0
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function refundStatusPill(p) {
  if (!p?.refunded) return null
  if (p.refundApproved === true) {
    return { label: p?.guestRefund?.issued ? 'Refunded' : 'Approved · awaiting payout', tone: 'bg-emerald-100 text-emerald-700' }
  }
  if (p.refundApproved === false) {
    return { label: 'Refund rejected', tone: 'bg-red-100 text-red-700' }
  }
  return { label: 'Marked for refund', tone: 'bg-orange-100 text-orange-700' }
}

function RefundsTab({
  order,
  guestName,
  rows,
  selection,
  selectedRows,
  previewGroups,
  previewByOrder,
  previewLoading,
  previewError,
  total,
  reason,
  setReason,
  notes,
  setNotes,
  onToggle,
  onSelectAll,
  onSelectOutOfStock,
  onClear,
  sending,
  marking,
  error,
  message,
  onMark,
  onSend,
}) {
  const refundableRows = rows.filter(r => !r.product.refunded)
  const historyRows = rows.filter(r => r.product.refunded)
  const hasRefundable = refundableRows.length > 0
  const selectedCount = selectedRows.length
  const emailCount = previewGroups.length
  const disableMark = selectedCount === 0 || !reason || sending || marking
  const disableSend = selectedCount === 0 || !reason || sending || marking || previewLoading

  // Friendly reason to surface as a tooltip / helper text when the primary
  // action is disabled so the operator can fix it without guessing.
  const markBlockReason = (() => {
    if (sending || marking) return 'Working on it…'
    if (selectedCount === 0) return 'Pick at least one item to refund.'
    if (!reason) return 'Select a reason — it applies to every checked item.'
    return ''
  })()

  const sendBlockReason = (() => {
    if (previewLoading) return 'Loading the exact refund email preview…'
    return markBlockReason
  })()

  return (
    <div className="space-y-4">
      {/* STICKY ACTION BAR — this is the primary commit control. It lives at
          the top so it's always visible, even as the selection list scrolls,
          and it holds the Reason picker (required) plus the Send + Mark
          buttons so there is exactly one place to act from. */}
      <div className="sticky top-0 z-10 -mx-6 -mt-6 mb-2 border-b border-red-200 bg-red-50/95 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-red-700">
              Refund queue
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {selectedCount > 0
                ? `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected · ${currency(total)} · ${emailCount} email${emailCount === 1 ? '' : 's'}`
                : 'No items selected yet'}
            </span>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`rounded-xl border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                !reason && selectedCount > 0
                  ? 'border-red-400 text-red-700 focus:border-red-500 focus:ring-red-500/30'
                  : 'border-gray-300 text-gray-900 focus:border-red-500 focus:ring-red-500/30'
              }`}
              title="Reason applies to every selected item"
            >
              {REFUND_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onMark}
              disabled={disableMark}
              title={disableMark ? markBlockReason : 'Flag the selected items for refund without sending a guest email yet'}
              className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {marking ? 'Marking…' : `Mark ${selectedCount || ''} for refund`.trim()}
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={disableSend}
              title={disableSend ? sendBlockReason : 'Marks + auto-approves + sends the exact previewed guest email(s)'}
              className="rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {sending
                ? 'Sending…'
                : `Send ${emailCount === 1 ? 'refund email' : `${emailCount} refund emails`} · ${currency(total)}`}
            </button>
          </div>
        </div>

        {/* Helper text rail: explicit blocker hint + status/error strips so
            operators always know why the button is gray and whether their
            last action succeeded. */}
        {(markBlockReason && selectedCount > 0) || error || message ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {markBlockReason && selectedCount > 0 ? (
              <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">
                {markBlockReason}
              </span>
            ) : null}
            {error ? (
              <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">
                {error}
              </span>
            ) : null}
            {message ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">
                {message}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* LEFT — selection + shared fields */}
        <section className="space-y-4">
        {/* Helper row */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            disabled={!hasRefundable}
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onSelectOutOfStock}
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
            disabled={!hasRefundable}
          >
            Select "refund" items
          </button>
          {selection.size > 0 ? (
            <button
              type="button"
              onClick={onClear}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
            >
              Clear ({selection.size})
            </button>
          ) : null}
          <span className="ml-auto text-xs text-gray-500">
            {refundableRows.length} refundable · {historyRows.length} already refunded
          </span>
        </div>

        {/* Items list */}
        <div className="overflow-hidden rounded-2xl border border-gray-200">
          <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
            {refundableRows.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                Everything on this order has already been refunded.
              </div>
            ) : (
              refundableRows.map(row => {
                const isChecked = selection.has(row.key)
                const p = row.product
                const line = (Number(p.price) || 0) * (Number(p.quantity) || 1)
                const prefersRefund = String(p.substituteOrRefund || '').toLowerCase() === 'refund'
                return (
                  <label
                    key={row.key}
                    className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      isChecked ? 'bg-red-50/60' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      checked={isChecked}
                      onChange={() => onToggle(row)}
                    />
                    {p.image ? (
                      <img src={p.image} alt="" className="h-10 w-10 flex-shrink-0 rounded-lg object-cover" />
                    ) : (
                      <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{p.name || 'Item'}</p>
                      <p className="text-xs text-gray-500">
                        #{row.orderUniqueId}{row.isAddon ? ' · add-on' : ''} · qty {p.quantity || 1} · {currency(line)}
                        {prefersRefund ? (
                          <span className="ml-1 inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                            refund
                          </span>
                        ) : null}
                      </p>
                    </div>
                  </label>
                )
              })
            )}
          </div>
        </div>

        {/* Shared notes (Reason lives in the sticky bar above) */}
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Notes (optional, shared across all selected)
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Extra context for support / audit"
            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
          />
        </div>

        {/* History (read-only) */}
        {historyRows.length > 0 ? (
          <details className="rounded-2xl border border-gray-200 bg-gray-50/50">
            <summary className="cursor-pointer px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Refund history ({historyRows.length})
            </summary>
            <ul className="divide-y divide-gray-100">
              {historyRows.map(row => {
                const pill = refundStatusPill(row.product)
                const p = row.product
                const line = (Number(p.price) || 0) * (Number(p.quantity) || 1)
                return (
                  <li key={row.key} className="flex items-center gap-3 px-4 py-2 text-sm">
                    {p.image ? (
                      <img src={p.image} alt="" className="h-8 w-8 flex-shrink-0 rounded-md object-cover opacity-70" />
                    ) : (
                      <div className="h-8 w-8 flex-shrink-0 rounded-md bg-gray-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-800 line-through">{p.name || 'Item'}</p>
                      <p className="text-xs text-gray-500">
                        #{row.orderUniqueId} · qty {p.quantity || 1} · {currency(line)}
                        {p.refundReason ? ` · ${p.refundReason}` : ''}
                        {p.refundedByName ? ` · by ${p.refundedByName}` : ''}
                      </p>
                    </div>
                    {pill ? (
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${pill.tone}`}>
                        {pill.label}
                      </span>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </details>
        ) : null}
      </section>

      {/* RIGHT — live email preview + totals + actions */}
      <section className="space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Guest email preview
          </p>
          <p className="mt-1 text-sm text-gray-800">
            {selectedCount > 0
              ? `${emailCount} guest email${emailCount === 1 ? '' : 's'} will be sent from this selection.`
              : 'Pick items on the left to load the exact guest email preview.'}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Reason and notes are stored on the order for ops and support. They are not included in the guest email.
          </p>
          {reason ? (
            <p className="mt-2 text-xs text-gray-500">
              Reason recorded: <span className="text-gray-700">{REFUND_REASONS.find(r => r.value === reason)?.label || reason}</span>
            </p>
          ) : null}
        </div>

        {previewError ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            {previewError} Showing a fallback summary below while the send action remains available.
          </div>
        ) : null}

        {selectedCount === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            Select refund items to preview the outgoing email.
          </div>
        ) : (
          previewGroups.map((group, index) => {
            const preview = previewByOrder[group.orderId]
            return (
              <div key={group.orderId} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-semibold text-gray-700">
                        Email {index + 1}
                      </span>
                      <span className="ml-2 text-gray-500">
                        Order #{group.orderUniqueId}{group.isAddon ? ' · add-on' : ''}
                      </span>
                    </div>
                    <span className="font-medium text-gray-700">
                      {group.rows.length} item{group.rows.length === 1 ? '' : 's'} · {currency(group.total)}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div>
                      <span className="font-medium text-gray-500">To:</span>{' '}
                      <span className="text-gray-900">{preview?.guestEmail || order.guestEmail || '—'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Subject:</span>{' '}
                      <span className="text-gray-900">{preview?.subject || 'Bellhopt Order Refunded Items'}</span>
                    </div>
                  </div>
                </div>

                {preview?.html ? (
                  <iframe
                    title={`Refund email preview for order ${group.orderUniqueId}`}
                    srcDoc={preview.html}
                    sandbox=""
                    className="h-[540px] w-full bg-white"
                  />
                ) : (
                  <RefundEmailFallbackPreview
                    guestName={guestName}
                    group={group}
                    loading={previewLoading}
                  />
                )}
              </div>
            )
          })
        )}
      </section>
      </div>
    </div>
  )
}

function RefundEmailFallbackPreview({ guestName, group, loading }) {
  return (
    <div className="space-y-3 px-4 py-4 text-sm text-gray-800">
      {loading ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-center text-sm text-gray-500">
          Loading the exact refund email preview…
        </div>
      ) : null}
      <p>Hi {guestName},</p>
      <p>
        We wanted to let you know that the following item{group.rows.length === 1 ? ' has' : 's have'} been
        refunded from your order:
      </p>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {group.rows.map((row) => {
          const p = row.product
          const line = (Number(p.price) || 0) * (Number(p.quantity) || 1)

          return (
            <li key={row.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="truncate text-gray-900">
                {p.name || 'Item'}
                <span className="ml-1 text-xs text-gray-500">× {p.quantity || 1}</span>
              </span>
              <span className="font-medium text-gray-900">{currency(line)}</span>
            </li>
          )
        })}
      </ul>
      <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2 text-sm">
        <span className="font-medium text-red-700">Total refunded</span>
        <span className="font-semibold text-red-700">{currency(group.total)}</span>
      </div>
      <p className="text-xs text-gray-500">
        The refund should land in the original payment method within 5–10 business days.
      </p>
    </div>
  )
}

function GuestDeliveryAddressCard({ data }) {
  if (!data) return null
  const lines = [data.line1, data.line2].filter(Boolean).join(', ')
  const cityRow = [data.city, data.state, data.zipCode].filter(Boolean).join(', ')
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <MapPinIcon className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
            Guest-provided delivery details
          </p>
          <h3 className="mt-0.5 font-semibold text-black">
            {data.propertyManager || 'Host / Property manager'}
          </h3>
          {lines ? <p className="mt-1 text-sm text-black">{lines}</p> : null}
          {cityRow ? <p className="text-sm text-gray-700">{cityRow}</p> : null}
          {data.entryInstructions ? (
            <div className="mt-3 rounded-xl bg-white/80 p-3 text-sm text-black ring-1 ring-amber-200">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                Entry instructions
              </p>
              <p className="mt-1 whitespace-pre-wrap">{data.entryInstructions}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
